import React, { useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StakeholderRoadmap from './StakeholderRoadmap';
import { callIRISRich, getIRISErrorMessage } from '../services/IRIS';
import {  AsanaTask,getAsanaTasks, bulkUpdateTasks, createAsanaTask, AsanaTaskData, deleteTasks, getEnumFieldConfig, EnumFieldConfigResponse, getTaskStories, addTaskStory, AsanaStory  } from '../services/asanaService';
import { getAggregatedL7Data, findL7ManagerByName, getAllL7Managers, L7ManagerData, getOrganizationData, clearOrganizationCache } from '../services/organizationS3Service';
import PortfolioSelector from '../components/PortfolioSelector';
import { useOrganizations } from '../hooks/useOrganizations';
import './TaskPrioritization.css';
import { AuthenticatedUserContext } from "../auth";
import { generateNarrativeForTasks, NarrativeResponse, formatNarrativeForDisplay } from '../utils/narrativeTaskUtility';

// ─── PM Workflow Banner ───────────────────────────────────────────────────────

const WORKFLOW_STEPS_TP = [
  { status: 'In Review',   cls: 'in-review',   desc: "New request arrives — PM is triaging" },
  { status: 'Not Started', cls: 'not-started',  desc: "Approved and queued — work hasn't begun" },
  { status: 'In Progress', cls: 'in-progress',  desc: 'Work has started' },
  { status: 'Blocked',     cls: 'blocked',      desc: 'Waiting on another team or dependency' },
  { status: 'Delayed',     cls: 'delayed',      desc: 'Missed planned date — still being worked on' },
  { status: 'Completed',   cls: 'complete',     desc: 'Shipped and done' },
  { status: 'Cancelled',   cls: 'cancelled',    desc: "Won't be built" },
  { status: 'On Hold',     cls: 'on-hold',      desc: 'Intentionally paused — not being worked on' },
];

const TP_BANNER_KEY = 'tp_workflow_banner_open';

const WorkflowBanner: React.FC = () => {
  const [isOpen, setIsOpen] = React.useState<boolean>(() => {
    try { const v = localStorage.getItem(TP_BANNER_KEY); return v === 'true'; } catch { return false; }
  });

  React.useEffect(() => {
    try { localStorage.setItem(TP_BANNER_KEY, String(isOpen)); } catch { /* ignore */ }
  }, [isOpen]);

  return (
    <div className={`workflow-banner${isOpen ? ' open' : ''}`}>
      <button
        className="workflow-banner-toggle"
        onClick={() => setIsOpen(v => !v)}
        type="button"
        aria-expanded={isOpen}
      >
        <span className="workflow-banner-icon">ℹ️</span>
        <span className="workflow-banner-title">PM Status Workflow Guide</span>
        <span className={`workflow-banner-chevron${isOpen ? ' open' : ''}`} aria-hidden="true">›</span>
      </button>
      {isOpen && (
        <div className="workflow-banner-body">
          <div className="workflow-steps">
            {WORKFLOW_STEPS_TP.map((step, i) => (
              <div key={step.status} className="workflow-step">
                {i > 0 && <span className="workflow-arrow" aria-hidden="true">→</span>}
                <span className={`workflow-status-badge ${step.cls}`}>
                  <span className="workflow-badge-dot" />
                  {step.status}
                </span>
                <span className="workflow-step-desc">{step.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
interface PrioritizationResult {
  Gid: string;
  mission_fit: string;
  op_decision: string;
  priority: string;
  notes: string;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc' | null;
}

interface TaskPrioritizationProps {
  onTasksLoaded?: (tasks: AsanaTask[]) => void;
}

interface Organization {
  id: string;
  name: string;
  asanaProject?: string;
}

const MISSION_FIT_OPTIONS = ['Aligned with Mission', 'Not Aligned', 'Not Applicable'];

const TaskPrioritization: React.FC<TaskPrioritizationProps> = ({ onTasksLoaded }) => {
  // Routing hooks
  const { orgId, portfolioId } = useParams<{ orgId: string; portfolioId?: string }>();
  const navigate = useNavigate();
  
  // Initialize useOrganizations to set global PAT and field mappings
  // This ensures the Asana API calls have the correct credentials
  // Must wait for loading to complete before fetching org data or tasks
  const { loading: orgsLoading } = useOrganizations();
  
  // Get organization data and resolve project ID
  const [organizationName, setOrganizationName] = useState<string>('');
  const [asanaProjectId, setAsanaProjectId] = useState<string>('');
  const [organizationError, setOrganizationError] = useState<string>('');
  
  useEffect(() => {
    const loadOrganizationData = async () => {
      if (orgId) {
        try {
          // Force refresh on first load to ensure we have the latest org data from S3
          // This is critical for direct URL navigation where cache may be empty
          const orgData = await getOrganizationData(true, 'TaskPrioritization-init');
          if (orgData && orgData.organizations) {
            const org = orgData.organizations.find(o => o.id === orgId || o.asanaProject === orgId);
            if (org) {
              // Check portfolios for asanaProjectId
              const portfolios = (org as any).portfolios as any[] | undefined;
              if (portfolios && portfolios.length > 0) {
                // If portfolioId is in URL, find that specific portfolio
                const targetPortfolio = portfolioId 
                  ? portfolios.find((p: any) => p.id === portfolioId)
                  : portfolios[0]; // Default to first portfolio
                
                if (targetPortfolio) {
                  setOrganizationName(`${org.name} → ${targetPortfolio.name}`);
                  setAsanaProjectId(targetPortfolio.asanaProjectId);
                  setOrganizationError('');
                } else {
                  setOrganizationError(`Portfolio "${portfolioId}" not found in organization "${org.name}".`);
                }
              } else if (org.asanaProject) {
                // Legacy: use org-level asanaProject
                setOrganizationName(org.name);
                setAsanaProjectId(org.asanaProject);
                setOrganizationError('');
              } else {
                setOrganizationError(`Organization "${org.name}" does not have any portfolios configured.`);
              }
            } else {
              setOrganizationError(`Organization with ID "${orgId}" not found.`);
            }
          } else {
            setOrganizationError('No organization data available. Please check your connection or contact support.');
          }
        } catch (error) {
          console.error('Failed to load organization data:', error);
          setOrganizationError('Failed to load organization data. Please try refreshing the page.');
        }
      }
    };
    
    // Wait for useOrganizations to finish loading (sets PAT and field mappings)
    if (!orgsLoading) {
      loadOrganizationData();
    }
  }, [orgId, orgsLoading]);
  const [tasks, setTasks] = useState<AsanaTask[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<AsanaTask[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isPrioritizing, setIsPrioritizing] = useState(false);
  const [prioritizationResults, setPrioritizationResults] = useState<PrioritizationResult[]>([]);
  
  // Tab navigation state
  const [activeTab, setActiveTab] = useState<'all' | 'new' | 'roadmap'>('all');

  // Handle tab switching with appropriate filter defaults
  const handleTabChange = (tab: 'all' | 'new' | 'roadmap') => {
    setActiveTab(tab);

    const currentYear = new Date().getFullYear().toString();

    if (tab === 'roadmap') {
      // Roadmap tab: P1/P2 decisions, current year, all statuses except Cancelled
      const p1p2Decisions = getP1P2Decisions();
      const allStatuses = Array.from(new Set(tasks.map(t => (t as any).status))).filter(Boolean) as string[];
      const nonCancelledStatuses = allStatuses.filter(s => !s.toLowerCase().includes('cancel'));
      setFilters(prev => ({
        ...prev,
        target_year: [currentYear],
        op_decision: p1p2Decisions,
        status: nonCancelledStatuses
      }));
      return;
    }

    if (tab === 'all') {
      // All Requests tab: Default to current year + P1/P2 decisions
      const p1p2Decisions = getP1P2Decisions();
      setFilters(prev => ({
        ...prev,
        target_year: [currentYear],
        op_decision: p1p2Decisions,
        status: []
      }));
    } else {
      // New Requests tab: Default to current year only (auto-filtering handles the rest)
      setFilters(prev => ({
        ...prev,
        target_year: [currentYear],
        op_decision: [],
        status: []
      }));
    }
  };
  
  const [selectedTaskForModal, setSelectedTaskForModal] = useState<AsanaTask | null>(null);
  const [isEditingModal, setIsEditingModal] = useState(false);
  const [modalEditData, setModalEditData] = useState<{
    name: string;
    problem_statement: string;
    desired_outcome: string;
    business_impact: string;
    theme: string;
    impact_metrics: string;
    target_year: string;
    request_type: string;
    rationale: string;
    launch_date: string;
  }>({
    name: '',
    problem_statement: '',
    desired_outcome: '',
    business_impact: '',
    theme: '',
    impact_metrics: '',
    target_year: '',
    request_type: '',
    rationale: '',
    launch_date: ''
  });
  const [isSavingModal, setIsSavingModal] = useState(false);

    const authenticatedUser = useContext(AuthenticatedUserContext);
  
  // Duplicate functionality state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [taskToDuplicate, setTaskToDuplicate] = useState<AsanaTask | null>(null);
  const [availableOrganizations, setAvailableOrganizations] = useState<Organization[]>([]);
  const [selectedTargetOrg, setSelectedTargetOrg] = useState<string>('');
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateSuccess, setDuplicateSuccess] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  
  // Delete functionality state
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  // Editable fields state
  const [taskChanges, setTaskChanges] = useState<Record<string, Partial<AsanaTask> & { 
    l7_manager_display_name?: string;
    op_decision?: string;
    mission_fit?: string;
  }>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Narrative functionality state
  const [isGeneratingNarrative, setIsGeneratingNarrative] = useState(false);
  const [narrativeResults, setNarrativeResults] = useState<NarrativeResponse | null>(null);
  const [showNarrativeModal, setShowNarrativeModal] = useState(false);

  // Comments/Stories functionality state
  const [taskComments, setTaskComments] = useState<AsanaStory[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);

  // Enum config state
  const [enumConfig, setEnumConfig] = useState<EnumFieldConfigResponse | null>(null);

  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Sorting state - default to sorting by created_at with newest first
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'created_at', direction: 'desc' });

  // GroupBy state
  const [groupByField, setGroupByField] = useState<'none' | 'op_decision' | 'program' | 'l7_manager' | 'impact_metrics'>('none');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // GroupBy functionality
  interface GroupData {
    name: string;
    tasks: AsanaTask[];
    totalEntitlement: number;
    totalSDE: number;
    taskCount: number;
  }

  const getGroupedData = (tasks: AsanaTask[], groupBy: string): GroupData[] | null => {
    if (groupBy === 'none') return null;
    
    const groups = new Map<string, GroupData>();
    
    tasks.forEach(task => {
      let groupKey: string;
      
      switch (groupBy) {
        case 'op_decision':
          groupKey = (task as any).op_decision || 'Unknown';
          break;
        case 'program':
          groupKey = (task as any).program_name || task.program || 'Unknown';
          break;
        case 'l7_manager':
          groupKey = (task as any).l7_sdm || (task as any).l7_manager || 'Unknown';
          break;
          case 'impact_metrics':
          groupKey = (task as any).impact_metrics || (task as any).impact_metrics || 'Unknown';
          break;
        default:
          groupKey = 'Unknown';
      }
      
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          name: groupKey,
          tasks: [],
          totalEntitlement: 0,
          totalSDE: 0,
          taskCount: 0
        });
      }
      
      const group = groups.get(groupKey)!;
      group.tasks.push(task);
      group.totalEntitlement += parseFloat((task as any).annualized_entitlement) || 0;
      group.totalSDE += parseFloat((task as any).hc_sde) || 0;
      group.taskCount++;
    });
    
    // Sort groups by total entitlement descending
    return Array.from(groups.values()).sort((a, b) => 
      b.totalEntitlement - a.totalEntitlement
    );
  };

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const handleGroupByChange = (value: 'none' | 'op_decision' | 'program' | 'l7_manager' | 'impact_metrics') => {
    setGroupByField(value);
    // Clear expanded groups when changing groupby field
    setExpandedGroups(new Set());
  };

  // OP Decision order for custom sorting - using first word for flexible matching
  const opDecisionOrder = {
    'New': 1,
    'Review': 2,
    'P1': 3,
    'P2': 4,
    'P3': 5,
    'P4': 6,
    'TBD': 7
  };

  // Helper function to get the first word of a decision for sorting
  const getDecisionFirstWord = (decision: string): string => {
    if (!decision) return 'TBD';
    return decision.split(' ')[0] || 'TBD';
  };

  // Helper function to get P1/P2 decisions dynamically
  const getP1P2Decisions = (): string[] => {
    const allDecisions = Array.from(new Set(tasks.map(task => (task as any).op_decision))).filter(Boolean);
    return allDecisions.filter(decision => {
      const firstWord = getDecisionFirstWord(decision);
      return firstWord === 'P1' || firstWord === 'P2';
    });
  };

  // Helper function to get default filters based on active tab
  const getDefaultFilters = () => {
    const currentYear = new Date().getFullYear().toString();
    
    return {
      name: '',
      program: [] as string[],
      theme: [] as string[],
      teamAssigned: [] as string[],
      l7_sdm: [] as string[],
      op_decision: [] as string[], // Start with empty, will be set by useEffect when tasks load
      mission_fit: [] as string[],
      target_year: [currentYear] as string[],
      request_type: [] as string[]
    };
  };

  const [filters, setFilters] = useState({
    name: '',
    program: [] as string[],
    theme: [] as string[],
    teamAssigned: [] as string[],
    l7_sdm: [] as string[],
    op_decision: [] as string[],
    mission_fit: [] as string[],
    target_year: [new Date().getFullYear().toString()] as string[],
    request_type: [] as string[],
    status: [] as string[]
  });

  // Sorting functions
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' | null = 'asc';
    
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }
    
    setSortConfig({ key, direction });
  };

  const getSortedTasks = (tasks: AsanaTask[]) => {
    if (!sortConfig.key || !sortConfig.direction) {
      return tasks;
    }

    return [...tasks].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Handle different field types
      switch (sortConfig.key) {
        case 'op_decision':
          const aDecisionFirstWord = getDecisionFirstWord((a as any).op_decision);
          const bDecisionFirstWord = getDecisionFirstWord((b as any).op_decision);
          aValue = opDecisionOrder[aDecisionFirstWord as keyof typeof opDecisionOrder] || 999;
          bValue = opDecisionOrder[bDecisionFirstWord as keyof typeof opDecisionOrder] || 999;
          break;
        case 'annualized_entitlement':
          aValue = parseFloat((a as any).annualized_entitlement) || 0;
          bValue = parseFloat((b as any).annualized_entitlement) || 0;
          break;
        case 'program':
          aValue = (a as any).program_name || a.program || '';
          bValue = (b as any).program_name || b.program || '';
          break;
        case 'teamAssigned':
          aValue = a.assignee?.name || a.teamAssigned || '';
          bValue = b.assignee?.name || b.teamAssigned || '';
          break;
        case 'l7_manager':
          aValue = (a as any).l7_manager || '';
          bValue = (b as any).l7_manager || '';
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          aValue = (a as any)[sortConfig.key] || '';
          bValue = (b as any)[sortConfig.key] || '';
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      let comparison = 0;
      if (aValue > bValue) {
        comparison = 1;
      } else if (aValue < bValue) {
        comparison = -1;
      }

      return sortConfig.direction === 'desc' ? comparison * -1 : comparison;
    });
  };

  const getSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-icon sort-icon-inactive">↕</span>;
    }
    
    if (sortConfig.direction === 'asc') {
      return <span className="sort-icon sort-icon-asc">↑</span>;
    } else if (sortConfig.direction === 'desc') {
      return <span className="sort-icon sort-icon-desc">↓</span>;
    }
    
    return <span className="sort-icon sort-icon-inactive">↕</span>;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedOutside = Object.keys(openDropdowns).every(key => {
        const ref = dropdownRefs.current[key];
        return !ref || !ref.contains(event.target as Node);
      });
      
      if (clickedOutside) {
        setOpenDropdowns({});
      }

    };

 

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);

    
  }, [openDropdowns]);

 

  // Create assignee lookup map from task data
  const assigneeLookup = React.useMemo(() => {
    const lookup: Record<string, { gid: string; name: string; resource_type: string }> = {};
    
    tasks.forEach(task => {
      if (task.assignee && task.assignee.name) {
        lookup[task.assignee.name] = {
          gid: task.assignee.gid,
          name: task.assignee.name,
          resource_type: task.assignee.resource_type
        };
      }
    });
    
    return lookup;
  }, [tasks]);

  // Create L7 manager lookup map from task data
  // L7 managers are enum fields, so we need to extract the enum GIDs from the raw custom field data
  const l7ManagerLookup = React.useMemo(() => {
    const lookup: Record<string, string> = {}; // name -> enum GID
    
    tasks.forEach(task => {
      const l7ManagerName = (task as any).l7_manager;
      if (l7ManagerName && task._customFields) {
        // Look for the l7_manager enum data in the raw custom fields
        const l7ManagerEnum = task._customFields.l7_manager_enum;
        if (l7ManagerEnum && l7ManagerEnum.gid) {
          lookup[l7ManagerName] = l7ManagerEnum.gid;
        }
      }
    });
    
    return lookup;
  }, [tasks]);

  // Memoize getAggregatedL7Data function to prevent unnecessary re-renders
  const memoizedGetAggregatedL7Data = useCallback(
    (l7ManagerNames: string[], orgId?: string) => getAggregatedL7Data(l7ManagerNames, orgId),
    []
  );

  // Memoize loadTasks function to prevent unnecessary re-renders
  const loadTasks = useCallback(async (forceRefresh: boolean = false, signal?: AbortSignal) => {
    // Only load tasks if we have a valid asanaProjectId
    if (!asanaProjectId) {
      console.log('No asanaProjectId available, skipping task load');
      return;
    }
    
    try {
      const tasksData = await getAsanaTasks(forceRefresh, asanaProjectId, signal);
      setTasks(tasksData);
      setFilteredTasks(tasksData);
      if (onTasksLoaded) {
        onTasksLoaded(tasksData);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Failed to load tasks:', error);
      }
    }
  }, [asanaProjectId, onTasksLoaded]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      // Force refresh when sync button is clicked
      await loadTasks(true);
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    
    const initLoad = async () => {
      try {
        await loadTasks(false, abortController.signal);
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError' && isMounted) {
          console.error('Failed to load tasks on init:', error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    initLoad();
    
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [orgId, loadTasks]);

  // Fetch enum config separately once asanaProjectId is resolved
  // This ensures we pass the actual Asana project ID (not orgId) so the backend
  // can fetch the correct custom field enum options for this specific project.
  useEffect(() => {
    if (!asanaProjectId) return;
    
    let isMounted = true;
    const abortController = new AbortController();
    
    const fetchEnumConfig = async () => {
      try {
        const enumConfigData = await getEnumFieldConfig(true, asanaProjectId, abortController.signal);
        if (isMounted) {
          setEnumConfig(enumConfigData);
          console.log('✅ Enum config loaded for project:', asanaProjectId, 
            Object.entries(enumConfigData).map(([k, v]) => `${k}: ${Object.keys((v as any)?.options || {}).length} options`).join(', '));
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError' && isMounted) {
          console.error('Failed to load enum config:', error);
        }
      }
    };
    
    fetchEnumConfig();
    
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [asanaProjectId]);

  // Set default filters when tasks are loaded
  useEffect(() => {
    if (tasks.length > 0) {
      const currentYear = new Date().getFullYear().toString();
      
      if (activeTab === 'all') {
        // All Requests tab: Default to current year + P1/P2 decisions
        const p1p2Decisions = getP1P2Decisions();
        setFilters(prev => ({
          ...prev,
          target_year: [currentYear],
          op_decision: p1p2Decisions
        }));
      } else {
        // New Requests tab: Default to current year only
        setFilters(prev => ({
          ...prev,
          target_year: [currentYear],
          op_decision: []
        }));
      }
    }
  }, [tasks.length, activeTab]); // Only run when tasks are loaded or tab changes

  // Auto-filtering function for "New Request" tab
  const newRequestsFilter = (task: AsanaTask) => {
    const opDecision = (task as any).op_decision;
    return !opDecision || 
           opDecision === 'New' || 
           opDecision === 'Review in Progress' ||
           opDecision === 'Under Review' ||
           opDecision.trim() === '';
  };

  // Debounced filter effect to prevent excessive re-renders
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      let filtered = tasks.filter(task => {
        // Apply auto-filtering for "New Request" tab
        if (activeTab === 'new' && !newRequestsFilter(task)) {
          return false;
        }

        // Handle OP Decision filtering with support for empty/unset values
        const taskOpDecision = (task as any).op_decision;
        const opDecisionMatch = filters.op_decision.length === 0 || 
          filters.op_decision.includes(taskOpDecision) ||
          (filters.op_decision.includes('Unset') && (!taskOpDecision || taskOpDecision.trim() === ''));

        // Handle Mission Fit filtering
        const taskMissionFit = (task as any).mission_fit;
        const missionFitMatch = filters.mission_fit.length === 0 || 
          filters.mission_fit.includes(taskMissionFit) ||
          (filters.mission_fit.includes('Unset') && (!taskMissionFit || taskMissionFit.trim() === ''));

        // Handle Target Year filtering
        const taskTargetYear = (task as any).target_year;
        const targetYearMatch = filters.target_year.length === 0 || 
          filters.target_year.includes(taskTargetYear) ||
          (filters.target_year.includes('Unset') && (!taskTargetYear || taskTargetYear.trim() === ''));

        // Handle Request Type filtering
        const taskRequestType = (task as any).request_type;
        const requestTypeMatch = filters.request_type.length === 0 || 
          filters.request_type.includes(taskRequestType) ||
          (filters.request_type.includes('Unset') && (!taskRequestType || taskRequestType.trim() === ''));

        // Handle Status filtering
        const taskStatus = (task as any).status;
        const statusMatch = filters.status.length === 0 ||
          filters.status.includes(taskStatus) ||
          (filters.status.includes('Unset') && (!taskStatus || taskStatus.trim() === ''));

        return (
          (filters.name === '' || task.name.toLowerCase().includes(filters.name.toLowerCase())) &&
          (filters.program.length === 0 || filters.program.includes((task as any).program_name || task.program)) &&
          (filters.theme.length === 0 || filters.theme.includes(task.theme)) &&
          (filters.teamAssigned.length === 0 || filters.teamAssigned.includes(task.assignee?.name || task.teamAssigned || '')) &&
          (filters.l7_sdm.length === 0 || filters.l7_sdm.includes((task as any).l7_sdm || (task as any).l7_manager)) &&
          opDecisionMatch &&
          missionFitMatch &&
          targetYearMatch &&
          requestTypeMatch &&
          statusMatch
        );
      });
      setFilteredTasks(filtered);
    }, 150); // 150ms debounce to prevent excessive re-renders
    
    return () => clearTimeout(timeoutId);
  }, [filters, tasks, activeTab]);

  // Calculate dashboard metrics with L7 manager data
  const [allL7Managers, setAllL7Managers] = useState<any[]>([]);
  const [l7Data, setL7Data] = useState<any>({
    coveragePercentage: 0,
    matchedCount: 0,
    totalCount: 0,
    totalTargetSDE: 0,
    totalActualSDE: 0,
    totalTargetHC: 0,
    totalActualHC: 0,
    totalRTB: 0
  });

  // Load organizations for duplicate functionality
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    
    const loadOrganizations = async () => {
      try {
        const orgData = await getOrganizationData();
        if (isMounted && orgData && orgData.organizations) {
          setAvailableOrganizations(orgData.organizations);
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError' && isMounted) {
          console.error('Failed to load organizations for duplicate:', error);
        }
      }
    };
    
    loadOrganizations();
    
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  // Load L7 manager data - combine organization data with backend task data
  // Force refresh organization data when TaskPrioritization loads to get latest updates
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    
    const loadL7Data = async () => {
      if (tasks.length === 0) return; // Wait for tasks to load first
      
      try {
        // Clear cache first to ensure fresh data
        clearOrganizationCache();
        // Force refresh organization data to get latest updates from other pages
        await getOrganizationData(true, 'TaskPrioritization'); // Force refresh when TaskPrioritization loads
        const orgL7Managers = await getAllL7Managers(orgId);
        
        if (!isMounted) return; // Check if component is still mounted
        
        // Get unique L7 SDMs from task data (backend)
        const taskL7Names = Array.from(new Set([
          ...tasks.map(task => (task as any).l7_sdm).filter(Boolean),
          ...tasks.map(task => (task as any).l7_manager).filter(Boolean)
        ])).sort();
        
        
        // Create combined L7 manager list starting with organization data
        const combinedL7Managers = [...orgL7Managers];
        
        // Add L7s from tasks that aren't in organization data
        taskL7Names.forEach(taskL7Name => {
          const existsInOrg = orgL7Managers.some(orgManager => 
            orgManager.name.toLowerCase().trim() === taskL7Name.toLowerCase().trim()
          );
          
          if (!existsInOrg) {
           
            // Create a basic L7ManagerData object for task-only L7s
            combinedL7Managers.push({
              name: taskL7Name,
              targetSDE: 0,
              actualSDE: 0,
              totalTargetHC: 0,
              totalActualHC: 0,
              rtb: 0,
              role: 'L7 Manager (from tasks)',
              email: ''
            });
          }
        });
        
       
        
        if (isMounted) {
          setAllL7Managers(combinedL7Managers);
        }
        
        // Calculate initial aggregated data ONLY for organization managers (not task-only L7s)
        const orgManagerNames = orgL7Managers.map(manager => manager.name);
        const aggregatedData = await getAggregatedL7Data(orgManagerNames, orgId);
        
        if (!isMounted) return; // Check again before setting state
        
      
        setL7Data(aggregatedData);
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError' && isMounted) {
          console.error('Error loading L7 data:', error);
        }
      }
    };
    
    loadL7Data();
    
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [orgId, tasks.length]); // Changed from tasks to tasks.length to prevent unnecessary re-runs

  // Separate useEffect to update L7 data when filters change (without calling API)
  useEffect(() => {
    let isMounted = true;
    
    const updateL7Data = async () => {
      if (allL7Managers.length === 0) return;
      
      try {
        // Only use organization L7 managers for HC calculations, filter out task-only L7s
        const orgManagerNames = allL7Managers
          .filter((manager: any) => !manager.role?.includes('from tasks'))
          .map((manager: any) => manager.name);
        const l7ManagerNames = filters.l7_sdm.length > 0 
          ? filters.l7_sdm.filter(name => orgManagerNames.includes(name)) // Only include org L7s in calculations
          : orgManagerNames;
        
        const aggregatedData = await getAggregatedL7Data(l7ManagerNames, orgId);
        
        if (!isMounted) return; // Check if component is still mounted
        
        
        setL7Data(aggregatedData);
      } catch (error) {
        if (isMounted) {
          console.error('Error updating L7 data:', error);
        }
      }
    };
    
    updateL7Data();
    
    return () => {
      isMounted = false;
    };
  }, [filters.l7_sdm, allL7Managers, orgId, memoizedGetAggregatedL7Data]); // Use memoized function
  
  // Memoize expensive dashboard metrics calculation
  const dashboardMetrics = useMemo(() => {
    // Helper function to safely parse numeric values
    const safeParseFloat = (value: any): number => {
      if (value === null || value === undefined || value === '') return 0;
      
      // If it's already a number, return it
      if (typeof value === 'number') return isNaN(value) ? 0 : value;
      
      // If it's a string, clean it and parse
      if (typeof value === 'string') {
        // Remove currency symbols, commas, and other non-numeric characters except decimal points
        const cleaned = value.replace(/[^\d.-]/g, '');
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
      }
      
      return 0;
    };

    return {
      totalEntitlement: filteredTasks.reduce((sum, task) => {
        // Only look at annualized_entitlement field as specified
        const entitlement = safeParseFloat((task as any).annualized_entitlement);
        
        // Debug logging for zero values when OP decisions are filtered
        if (entitlement === 0 && filters.op_decision.length > 0) {
         
        }
        
        return sum + entitlement;
      }, 0),
      totalHcSde: filteredTasks
        .filter(t => {
          const opDecision = (t as any).op_decision;
          const firstWord = getDecisionFirstWord(opDecision);
          return firstWord !== 'P3' && firstWord !== 'P4';
        })
        .reduce((sum, task) => {
          const hcSde = parseFloat((task as any).hc_sde) || 0;
          return sum + hcSde;
        }, 0),
      totalTasks: filteredTasks.length,
      p1OpPriority: filteredTasks
        .filter(t => {
          const opDecision = (t as any).op_decision;
          const firstWord = getDecisionFirstWord(opDecision);
          return firstWord === 'P1';
        })
        .reduce((sum, task) => {
          const hcSde = parseFloat((task as any).hc_sde) || 0;
          return sum + hcSde;
        }, 0),
      p2OpPriority: filteredTasks
        .filter(t => {
          const opDecision = (t as any).op_decision;
          const firstWord = getDecisionFirstWord(opDecision);
          return firstWord === 'P2';
        })
        .reduce((sum, task) => {
          const hcSde = parseFloat((task as any).hc_sde) || 0;
          return sum + hcSde;
        }, 0),
      BTLOpPriority: filteredTasks
        .filter(t => {
          const opDecision = (t as any).op_decision;
          const firstWord = getDecisionFirstWord(opDecision);
          return firstWord === 'P3' || firstWord === 'P4';
        })
        .reduce((sum, task) => {
          const hcSde = parseFloat((task as any).hc_sde) || 0;
          return sum + hcSde;
        }, 0),
      tbdTasks: filteredTasks.filter(t => {
        const opDecision = (t as any).op_decision;
        return !opDecision || opDecision.trim() === '' || opDecision === 'TBD';
      }).length,
      // L7 Manager Organization Data
      l7Coverage: l7Data.coveragePercentage,
      l7MatchedCount: l7Data.matchedCount,
      l7TotalCount: l7Data.totalCount,
      targetSDE: l7Data.totalTargetSDE,
      actualSDE: l7Data.totalActualSDE,
      targetHC: l7Data.totalTargetHC,
      actualHC: l7Data.totalActualHC,
      rtbHC: l7Data.totalRTB
    };
  }, [filteredTasks, l7Data, filters.op_decision]);

  const handleTaskSelection = (taskGid: string) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskGid)) {
      newSelected.delete(taskGid);
    } else {
      newSelected.add(taskGid);
    }
    setSelectedTasks(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTasks.size === filteredTasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(filteredTasks.map(task => task.gid)));
    }
  };

  const handlePrioritize = async () => {
    if (selectedTasks.size === 0) {
      alert('Please select at least one task to prioritize');
      return;
    }

    setIsPrioritizing(true);
    try {
      const selectedTasksData = tasks.filter(task => selectedTasks.has(task.gid));
      const tasksForPrioritization = selectedTasksData.map(task => ({
        gid: task.gid,
        name: task.name,
        problem_statement: task.problem_statement,
        desired_outcome: task.desired_outcome,
        business_impact: task.business_impact,
        annualized_entitlement: task.annualized_entitlement,
        conversation_summary: task.conversation_summary
      }));

      // Get organization mission goals and key priorities for context
      const orgData = await getOrganizationData();
      let organizationContext = '';
      
      if (orgData && orgData.organizations.length > 0) {
        const activeMissionGoals = orgData.organizations
          .flatMap(org => org.missionGoals || [])
          .filter(goal => goal.status === 'active');
        
        const keyPriorities = orgData.organizations
          .flatMap(org => {
            // Read from portfolios first (new model), fall back to org-level (legacy)
            const portfolios = (org as any).portfolios as any[] | undefined;
            if (portfolios && portfolios.length > 0) {
              return portfolios.flatMap((p: any) => p.keyPriorities || []);
            }
            return org.keyPriorities || [];
          })
          .filter(priority => priority.trim());

        const tenets = orgData.organizations
          .flatMap(org => {
            // Read from portfolios first (new model), fall back to org-level (legacy)
            const portfolios = (org as any).portfolios as any[] | undefined;
            if (portfolios && portfolios.length > 0) {
              return portfolios.flatMap((p: any) => p.tenets || []);
            }
            return org.tenets || [];
          })
          .filter(tenet => tenet.trim());
        
        if (activeMissionGoals.length > 0 || keyPriorities.length > 0 || tenets.length > 0) {
          organizationContext = `

ORGANIZATION CONTEXT:
Use this organizational context to inform your prioritization decisions:`;

          if (activeMissionGoals.length > 0) {
            organizationContext += `

ACTIVE MISSION GOALS:
${activeMissionGoals.map(goal => `
• ${goal.title}
  Description: ${goal.description}
  Tags: ${goal.tags.join(', ')}
`).join('')}`;
          }

          if (keyPriorities.length > 0) {
            organizationContext += `

KEY ORGANIZATIONAL PRIORITIES:
${keyPriorities.map(priority => `• ${priority}`).join('\n')}`;
          }

          if (tenets.length > 0) {
            organizationContext += `

ORGANIZATIONAL TENETS:
${tenets.map(tenet => `• ${tenet}`).join('\n')}`;
          }

          organizationContext += `

Prioritize tasks that align with these mission goals, key priorities, and organizational tenets higher.`;
        }
      }

      const prompt = `Prioritize these requests and respond with ONLY a JSON array. Do not ask more questions, or include any explanatory text, markdown formatting, or additional commentary. Return only the raw JSON array in this exact format:

[
  {
    "Gid": "TASK-XXX",
    "mission_fit": "Aligned with Mission|Not Aligned|Not Applicable",
    "op_decision": "P1 - ATL|P2 - ATL|P3 - BTL|P4 - BTL" ,
    "notes": "brief explanation"
  }
]${organizationContext}

Tasks to prioritize:
${JSON.stringify(tasksForPrioritization, null, 2)}`;
      
      const response = await callIRISRich({
        userMessage: prompt,
        stage: 'finaljson',
        agentId: 'eec5ab1f-deb7-4ef3-91ed-741193debbee'
      });

        
     

      let jsonString = response;
      
      if (typeof response === 'object' && response !== null && 'response' in response) {
        jsonString = (response as any).response;
      }
      
      let cleanedResponse = jsonString.trim();
      
      if (cleanedResponse.includes('```json')) {
        cleanedResponse = cleanedResponse
          .replace(/^[\s\S]*?```json\s*/, '')
          .replace(/\s*```[\s\S]*$/, '')
          .trim();
      }
      
      if (cleanedResponse.startsWith('{') && !cleanedResponse.startsWith('[')) {
        cleanedResponse = `[${cleanedResponse}]`;
      } else if (!cleanedResponse.startsWith('[')) {
        const jsonMatch = cleanedResponse.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          cleanedResponse = jsonMatch[0];
        }
      }
      
     

      const results: PrioritizationResult[] = JSON.parse(cleanedResponse);
      setPrioritizationResults(results);
      
      // Instead of updating tasks directly, add the changes to taskChanges to show as pending
      const newTaskChanges = { ...taskChanges };
      let hasNewChanges = false;
      
      results.forEach(result => {
        const task = tasks.find(t => t.gid === result.Gid);
        if (task) {
          // Only update if the values are different from current values
          const currentOpDecision = (task as any).op_decision || '';
          const currentMissionFit = (task as any).mission_fit || '';
          
          if (result.op_decision !== currentOpDecision || result.mission_fit !== currentMissionFit) {
            newTaskChanges[result.Gid] = {
              ...newTaskChanges[result.Gid],
              op_decision: result.op_decision,
              mission_fit: result.mission_fit,
              rationale: `[Prioritization - ${new Date().toISOString()}]: ${result.notes}`
            };
            hasNewChanges = true;
          }
        }
      });
      
      if (hasNewChanges) {
        setTaskChanges(newTaskChanges);
        setHasUnsavedChanges(true);
      }
      
      setSelectedTasks(new Set());
      
    } catch (error) {
      console.error('Prioritization failed:', error);
      const errorMessage = getIRISErrorMessage(error, 'Task prioritization');
      alert(errorMessage);
    } finally {
      setIsPrioritizing(false);
    }
  };

  // Generate narrative for selected tasks
  const handleGenerateNarrative = async () => {
    if (selectedTasks.size === 0) {
      alert('Please select at least one task to generate narrative');
      return;
    }

    setIsGeneratingNarrative(true);
    try {
      const selectedTasksData = tasks.filter(task => selectedTasks.has(task.gid));
      
      // Prepare organization context from existing data
      const orgData = await getOrganizationData();
      
      // Find the specific organization by ID, not just [0]
      const currentOrg = orgData?.organizations?.find(org => 
        org.id === orgId || org.asanaProject === orgId
      );
      
      // Read from portfolio level first (new model), fall back to org-level (legacy)
      const portfolios = (currentOrg as any)?.portfolios as any[] | undefined;
      const activePortfolio = portfolios && portfolios.length > 0 ? portfolios[0] : null;
      
      const organizationContext = {
        name: organizationName,
        missionGoals: activePortfolio?.missionGoals || currentOrg?.missionGoals || [],
        keyPriorities: activePortfolio?.keyPriorities || currentOrg?.keyPriorities || [],
        tenets: activePortfolio?.tenets || currentOrg?.tenets || [],
        charter: activePortfolio?.charter || currentOrg?.charter || ''
      };

      // Call the narrative utility
      const response = await generateNarrativeForTasks({
        tasks: selectedTasksData,
        organizationContext,
        l7Data: {
          totalTargetSDE: l7Data.totalTargetSDE,
          totalActualSDE: l7Data.totalActualSDE,
          totalTargetHC: l7Data.totalTargetHC,
          totalActualHC: l7Data.totalActualHC,
          totalRTB: l7Data.totalRTB
        },
        organizationName
      });

      setNarrativeResults(response);
      setShowNarrativeModal(true);
      setSelectedTasks(new Set()); // Clear selection
    } catch (error) {
      console.error('Narrative generation failed:', error);
      const errorMessage = getIRISErrorMessage(error, 'Narrative generation');
      alert(errorMessage);
    } finally {
      setIsGeneratingNarrative(false);
    }
  };

  const getUniqueValues = (field: keyof AsanaTask): string[] => {
    return Array.from(new Set(tasks.map(task => String(task[field])))).filter(Boolean).sort();
  };

  const handleMultiSelectChange = (field: keyof typeof filters, value: string) => {
    if (field === 'name') return;
    
    const currentValues = filters[field] as string[];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    setFilters({...filters, [field]: newValues});
  };

  const toggleDropdown = (field: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const clearFilter = (field: keyof typeof filters) => {
    if (field === 'name') {
      setFilters({...filters, [field]: ''});
    } else {
      setFilters({...filters, [field]: []});
    }
  };

  // Generic field update handler
  const handleFieldChange = async (taskGid: string, field: keyof AsanaTask | string, value: any) => {
    // Special handling for L7 manager field - store name for UI, email for backend
    if (field === 'l7_manager') {
      const task = tasks.find(t => t.gid === taskGid);
      let emailToSend = value;
      
      // First try to get email from task data (if backend provided it)
      if (task && (task as any).l7_manager_email && (task as any).l7_manager === value) {
        emailToSend = (task as any).l7_manager_email;
       
      } else {
        // Fallback to Organization service lookup
        try {
          const l7ManagerData = await findL7ManagerByName(value, orgId);
          if (l7ManagerData && l7ManagerData.email) {
            emailToSend = l7ManagerData.email;
             
          } else {
            console.warn(`⚠️ No email found for L7 manager '${value}', sending name as fallback`);
            emailToSend = value; // Fallback to name if no email found
          }
        } catch (error) {
          console.error('Error looking up L7 manager:', error);
          emailToSend = value; // Fallback to name on error
        }
      }
      
      // Store both the display name (for UI) and email (for backend)
      setTaskChanges(prev => ({
        ...prev,
        [taskGid]: {
          ...prev[taskGid],
          [field]: emailToSend, // Backend gets email
          l7_manager_display_name: value // UI shows name
        }
      }));
    } else {
      // Handle other fields normally
      setTaskChanges(prev => {
        const newChanges = {
          ...prev,
          [taskGid]: {
            ...prev[taskGid],
            [field]: value
          }
        };
        
        // Special handling for assignee fields - sync both teamAssigned and assignee
        if (field === 'teamAssigned') {
          // Use the assignee lookup to get the complete assignee object with correct GID
          const assigneeObject = value ? assigneeLookup[value] || null : null;
          newChanges[taskGid] = {
            ...newChanges[taskGid],
            assignee: assigneeObject
          };
        }
        
        return newChanges;
      });
    }
    
    setHasUnsavedChanges(true);
  };

  // Get current field value (with changes applied)
  const getCurrentFieldValue = (task: AsanaTask, field: keyof AsanaTask | string) => {
    const changes = taskChanges[task.gid];
    
    // Special handling for assignee fields
    if (field === 'teamAssigned') {
      if (changes && field in changes) {
        return (changes as any)[field];
      }
      // Prioritize assignee.name over teamAssigned
      return task.assignee?.name || task.teamAssigned || '';
    }
    
    // Special handling for OP Decision field
    if (field === 'op_decision') {
      if (changes && field in changes) {
        return (changes as any)[field];
      }
      // Only use op_decision from backend
      return (task as any).op_decision || '';
    }
    
    // Special handling for L7 Manager/SDM field - always show name in UI
    if (field === 'l7_manager') {
      if (changes && 'l7_manager_display_name' in changes) {
        // If we have a display name stored, use that for UI
        return (changes as any).l7_manager_display_name;
      }
      // Check both l7_sdm and l7_manager from backend
      return (task as any).l7_sdm || (task as any).l7_manager || '';
    }
    
    return changes && field in changes ? (changes as any)[field] : (task as any)[field];
  };

  // Save changes to server
  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges || Object.keys(taskChanges).length === 0) return;
    
    setIsSaving(true);
    try {
      const updates = Object.entries(taskChanges).map(([gid, fields]) => ({
        gid,
        fields
      }));
      
      await bulkUpdateTasks(updates, orgId);
      
      // Apply changes to local state
      setTasks(prev => prev.map(task => {
        const changes = taskChanges[task.gid];
        return changes ? { ...task, ...changes } : task;
      }));
      
      // Clear changes
      setTaskChanges({});
      setHasUnsavedChanges(false);
      
      alert('Changes saved successfully!');
      
      // Sync after save
      await handleSync();
    } catch (error) {
      console.error('Failed to save changes:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Render editable dropdown
  const renderEditableDropdown = (task: AsanaTask, field: keyof AsanaTask | string, options: string[]) => {
    const currentValue = getCurrentFieldValue(task, field) as string;
    const hasChanges = taskChanges[task.gid] && field in taskChanges[task.gid];
    
    return (
      <select
        value={currentValue || ''}
        onChange={(e) => handleFieldChange(task.gid, field, e.target.value)}
        className={`editable-select ${hasChanges ? 'has-changes' : ''}`}
      >
        <option value="">-</option>
        {options.map(option => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    );
  };

  // Render editable date picker
  const renderEditableDatePicker = (task: AsanaTask, field: keyof AsanaTask | string) => {
    const currentValue = getCurrentFieldValue(task, field) as string;
    const hasChanges = taskChanges[task.gid] && field in taskChanges[task.gid];
    // Normalize to YYYY-MM-DD for the input value
    const inputValue = currentValue ? String(currentValue).split('T')[0] : '';

    return (
      <input
        type="date"
        value={inputValue}
        onChange={(e) => handleFieldChange(task.gid, field, e.target.value || null)}
        className={`editable-input editable-date ${hasChanges ? 'has-changes' : ''}`}
        style={{ minWidth: '130px' }}
      />
    );
  };

  // Render editable number input
  const renderEditableNumber = (task: AsanaTask, field: keyof AsanaTask) => {
    const currentValue = getCurrentFieldValue(task, field) as number;
    const hasChanges = taskChanges[task.gid] && field in taskChanges[task.gid];
    
    return (
      <input
        type="number"
        step="0.1"
        value={currentValue === null || currentValue === undefined ? 0 : currentValue}
        onChange={(e) => {
          // Always send a number - if empty or invalid, send 0
          const numValue = parseFloat(e.target.value);
          handleFieldChange(task.gid, field, isNaN(numValue) ? 0 : numValue);
        }}
        className={`editable-input ${hasChanges ? 'has-changes' : ''}`}
      />
    );
  };

  const formatCompactCurrency = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  } else {
    return `$${value.toFixed(0)}`;
  }
};

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const formatDateTimeDetailed = (dateString: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Age calculation and indicator functions
  const getTaskAge = (createdAt: string): { days: number; category: 'new' | 'attention' | 'urgent' | 'overdue' } => {
    const daysDiff = Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDiff <= 7) return { days: daysDiff, category: 'new' };
    if (daysDiff <= 14) return { days: daysDiff, category: 'attention' };
    if (daysDiff <= 30) return { days: daysDiff, category: 'urgent' };
    return { days: daysDiff, category: 'overdue' };
  };

  const renderAgeIndicator = (createdAt: string, task: AsanaTask) => {
    const age = getTaskAge(createdAt);
    const opDecision = (task as any).op_decision;
    
    // Check if status is "new" or "review in progress" (case insensitive)
    const isNewOrReviewInProgress = !opDecision || 
      opDecision.toLowerCase().includes('new') || 
      opDecision.toLowerCase().includes('review in progress') ||
      opDecision.toLowerCase().includes('under review') ||
      opDecision.trim() === '';
    
    // If status is new or review in progress, use green; otherwise use red for older tasks
    let displayCategory = age.category;
    if (!isNewOrReviewInProgress && age.category !== 'new') {
      displayCategory = 'urgent'; // Force red color for non-new/non-review tasks that are older
    }
    
    const indicators = {
      new: { dot: '🟢', label: 'New', title: `${age.days} days old - New request` },
      attention: { dot: '🟡', label: 'Needs Attention', title: `${age.days} days old - Needs attention` },
      urgent: { dot: '🔴', label: 'Urgent', title: `${age.days} days old - Urgent review needed` },
      overdue: { dot: '⚫', label: 'Overdue', title: `${age.days} days old - Overdue for review` }
    };
    
    const indicator = indicators[displayCategory];
    
    return (
      <div className={`age-indicator age-${displayCategory}`} title={indicator.title}>
        <span className="age-dot">{indicator.dot}</span>
        <span className="age-days">{age.days}d</span>
      </div>
    );
  };

  // Duplicate functionality
  const convertTaskToAsanaTaskData = (task: AsanaTask, targetOrgId?: string): AsanaTaskData => {
    // Check if we're copying to a different organization
    const isCopyingToAnotherOrg = targetOrgId && targetOrgId !== orgId;
    
    // Get the original team assignment
    let  team = task.assignee?.name || task.teamAssigned || '';
    
    // If copying to another org, remove L7 SDM assignments from team assignment
    if (isCopyingToAnotherOrg &&  team) {
      // Check if the assigned team member is an L7 SDM
      const isL7SDM = allL7Managers.some(manager => 
        manager.name.toLowerCase().trim() ===  team.toLowerCase().trim() &&
        (manager.role.toLowerCase().includes('sdm') || 
         manager.role.toLowerCase().includes('software development manager'))
      );
      
      if (isL7SDM) {
      
         team = ''; // Remove the L7 SDM assignment
      }
    }
    
    // Create the base task data
    const taskData: any = {
      title: `Copy of ${task.name}`,
      problem_statement: task.problem_statement || 'Please review and update problem statement',
      business_impact: task.business_impact || 'Please review and update business impact',
      annualized_entitlement: (task as any).annualized_entitlement?.toString() || '0',
      impact_metrics: (task as any).impact_metrics || '',
      desired_outcome: task.desired_outcome || 'Please review and update desired outcome',
      site_types: (task as any).site_types || '',
      theme: task.theme || '',
      program_name: task.program || '',
      impacted_region: (task as any).impacted_region || 'NA',
      aft_team:  team,
      created_by: authenticatedUser?.email,
      target_year: (task as any).target_year || '2024',
      op_decision: (task as any).op_decision || 'New',
      mission_fit: (task as any).mission_fit || 'Not Applicable',
 
  
      notes: `Duplicated from: ${task.permalink_url || 'Original task'}\nOriginal GID: ${task.gid}\nDuplicated on: ${new Date().toISOString()}`
    };
    
    // Handle L7 manager/SDM field assignments
    const originalL7Manager = (task as any).l7_manager || (task as any).l7_sdm;
    if (originalL7Manager) {
      if (isCopyingToAnotherOrg) {
       
        // Don't include L7 manager field in the copied task
        // Add note about removed L7 assignments
        taskData.notes += `\nNote: L7 SDM assignments removed when copying to another organization`;
        if ( team !== (task.assignee?.name || task.teamAssigned || '')) {
          taskData.notes += ` (team assignment: '${task.assignee?.name || task.teamAssigned}')`;
        }
        taskData.notes += ` (L7 manager: '${originalL7Manager}')`;
      } else {
        // Include L7 manager field when copying within the same org
       
        taskData.l7_manager = originalL7Manager;
      }
    }
    
    return taskData;
  };

  const getProjectIdForOrg = (orgId: string): string | null => {
    const org = availableOrganizations.find(o => o.id === orgId);
    return org?.asanaProject || null;
  };

  const handleDuplicateSelected = async () => {
    if (selectedTasks.size === 0) {
      setDuplicateError('Please select at least one task to duplicate');
      return;
    }

    if (!orgId) {
      setDuplicateError('No organization selected');
      return;
    }

    setDuplicateLoading(true);
    setDuplicateError(null);
    setDuplicateSuccess(null);
    
    try {
      const selectedTasksData = tasks.filter(task => selectedTasks.has(task.gid));
      let successCount = 0;
      let failCount = 0;

      for (const task of selectedTasksData) {
        try {
          const taskData = convertTaskToAsanaTaskData(task);
          const projectId = getProjectIdForOrg(orgId);
          
          const result = await createAsanaTask(taskData, projectId || undefined);
          
          if (result.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`Failed to duplicate task ${task.gid}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        setDuplicateSuccess(`Successfully duplicated ${successCount} task${successCount > 1 ? 's' : ''}${failCount > 0 ? ` (${failCount} failed)` : ''}`);
        // Refresh tasks to show the new duplicates
        await loadTasks(true);
        // Clear selection after successful duplication
        setSelectedTasks(new Set());
      } else {
        setDuplicateError(`Failed to duplicate ${failCount} task${failCount > 1 ? 's' : ''}`);
      }
    } catch (error) {
      console.error('Bulk duplicate failed:', error);
      setDuplicateError(error instanceof Error ? error.message : 'Failed to duplicate tasks');
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleDuplicateSelectedToAnotherOrg = () => {
    if (selectedTasks.size === 0) {
      setDuplicateError('Please select at least one task to duplicate');
      return;
    }

    // For bulk duplicate to another org, we'll use the first selected task as reference
    const firstSelectedTask = tasks.find(task => selectedTasks.has(task.gid));
    if (firstSelectedTask) {
      setTaskToDuplicate(firstSelectedTask);
      setSelectedTargetOrg('');
      setShowDuplicateModal(true);
      setDuplicateError(null);
      setDuplicateSuccess(null);
    }
  };

  const confirmDuplicateToAnotherOrg = async () => {
    if (!selectedTargetOrg) {
      setDuplicateError('Please select a target organization');
      return;
    }

    if (selectedTasks.size === 0) {
      setDuplicateError('No tasks selected for duplication');
      return;
    }

    setDuplicateLoading(true);
    setDuplicateError(null);
    setDuplicateSuccess(null);
    
    try {
      // selectedTargetOrg is now the asanaProjectId directly from the portfolio dropdown
      const projectId = selectedTargetOrg;
      
      if (!projectId) {
        setDuplicateError('Selected portfolio does not have an Asana project configured');
        return;
      }

      const selectedTasksData = tasks.filter(task => selectedTasks.has(task.gid));
      let successCount = 0;
      let failCount = 0;

      for (const task of selectedTasksData) {
        try {
          const taskData = convertTaskToAsanaTaskData(task, selectedTargetOrg);
          const result = await createAsanaTask(taskData, projectId || undefined);
          
          if (result.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`Failed to duplicate task ${task.gid} to another org:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        const targetOrgName = availableOrganizations.find(org => org.id === selectedTargetOrg)?.name || 'Selected organization';
        setDuplicateSuccess(`Successfully duplicated ${successCount} task${successCount > 1 ? 's' : ''} to ${targetOrgName}${failCount > 0 ? ` (${failCount} failed)` : ''}`);
        setShowDuplicateModal(false);
        setTaskToDuplicate(null);
        setSelectedTargetOrg('');
        // Clear selection after successful duplication
        setSelectedTasks(new Set());
      } else {
        setDuplicateError(`Failed to duplicate ${failCount} task${failCount > 1 ? 's' : ''} to the selected organization`);
      }
    } catch (error) {
      console.error('Bulk duplicate to another org failed:', error);
      setDuplicateError(error instanceof Error ? error.message : 'Failed to duplicate tasks');
    } finally {
      setDuplicateLoading(false);
    }
  };

  const closeDuplicateModal = () => {
    setShowDuplicateModal(false);
    setTaskToDuplicate(null);
    setSelectedTargetOrg('');
    setDuplicateError(null);
    setDuplicateSuccess(null);
  };

  // Modal edit functionality
  const handleEditModalTask = () => {
    if (!selectedTaskForModal) return;
    
    setModalEditData({
      name: selectedTaskForModal.name || '',
      problem_statement: selectedTaskForModal.problem_statement || '',
      desired_outcome: selectedTaskForModal.desired_outcome || '',
      business_impact: selectedTaskForModal.business_impact || '',
      theme: selectedTaskForModal.theme || '',
      impact_metrics: (selectedTaskForModal as any).impact_metrics || '',
      target_year: (selectedTaskForModal as any).target_year || '',
      request_type: (selectedTaskForModal as any).request_type || '',
      rationale: (selectedTaskForModal as any).rationale || '',
      launch_date: (selectedTaskForModal as any).launch_date ? String((selectedTaskForModal as any).launch_date).split('T')[0] : '',
    });
    setIsEditingModal(true);
  };

  const handleCancelModalEdit = () => {
    setIsEditingModal(false);
    setModalEditData({
      name: '',
      problem_statement: '',
      desired_outcome: '',
      business_impact: '',
      theme: '',
      impact_metrics: '',
      target_year: '',
      request_type: '',
      rationale: '',
      launch_date: ''
    });
  };

  const handleSaveModalEdit = async () => {
    if (!selectedTaskForModal) return;
    
    setIsSavingModal(true);
    try {
      // Only include fields that have values and are safe to update
      const fieldsToUpdate: any = {};
      
      if (modalEditData.name && modalEditData.name !== selectedTaskForModal.name) {
        fieldsToUpdate.name = modalEditData.name;
      }
      if (modalEditData.problem_statement !== selectedTaskForModal.problem_statement) {
        fieldsToUpdate.problem_statement = modalEditData.problem_statement;
      }
      if (modalEditData.desired_outcome !== selectedTaskForModal.desired_outcome) {
        fieldsToUpdate.desired_outcome = modalEditData.desired_outcome;
      }
      if (modalEditData.business_impact !== selectedTaskForModal.business_impact) {
        fieldsToUpdate.business_impact = modalEditData.business_impact;
      }
      if (modalEditData.theme !== selectedTaskForModal.theme) {
        fieldsToUpdate.theme = modalEditData.theme;
      }
      if (modalEditData.impact_metrics !== ((selectedTaskForModal as any).impact_metrics || (selectedTaskForModal as any).impact_metrics || '')) {
        fieldsToUpdate.impact_metrics = modalEditData.impact_metrics;
      }
      if (modalEditData.target_year !== ((selectedTaskForModal as any).target_year || '')) {
        fieldsToUpdate.target_year = modalEditData.target_year;
      }
        if (modalEditData.request_type !== ((selectedTaskForModal as any).request_type || '')) {
        fieldsToUpdate.request_type = modalEditData.request_type;
      }
      if (modalEditData.rationale !== ((selectedTaskForModal as any).rationale || '')) {
        fieldsToUpdate.rationale = modalEditData.rationale;
      }
      const existingLaunchDate = (selectedTaskForModal as any).launch_date
        ? String((selectedTaskForModal as any).launch_date).split('T')[0]
        : '';
      if (modalEditData.launch_date !== existingLaunchDate) {
        fieldsToUpdate.launch_date = modalEditData.launch_date || null;
      }
      
      const updates = [{
        gid: selectedTaskForModal.gid,
        fields: fieldsToUpdate
      }];
      
      await bulkUpdateTasks(updates, orgId);
      
      // Update local state (include ALL edited fields so the UI reflects the save immediately)
      setTasks(prev => prev.map(task => 
        task.gid === selectedTaskForModal.gid 
          ? { 
              ...task, 
              name: modalEditData.name,
              problem_statement: modalEditData.problem_statement,
              desired_outcome: modalEditData.desired_outcome,
              business_impact: modalEditData.business_impact,
              theme: modalEditData.theme,
              impact_metrics: modalEditData.impact_metrics,
              target_year: modalEditData.target_year,
              request_type: modalEditData.request_type,
              rationale: modalEditData.rationale,
              launch_date: modalEditData.launch_date || (task as any).launch_date,
            }
          : task
      ));
      
      // Update the modal task data
      setSelectedTaskForModal(prev => prev ? {
        ...prev,
        name: modalEditData.name,
        problem_statement: modalEditData.problem_statement,
        desired_outcome: modalEditData.desired_outcome,
        business_impact: modalEditData.business_impact,
        theme: modalEditData.theme,
        impact_metrics: modalEditData.impact_metrics,
        target_year: modalEditData.target_year,
        request_type: modalEditData.request_type,
        rationale: modalEditData.rationale,
        launch_date: modalEditData.launch_date || (prev as any).launch_date,
      } : null);
      
      setIsEditingModal(false);
      alert('Task updated successfully!');
      
    } catch (error) {
      console.error('Failed to save modal edits:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSavingModal(false);
    }
  };

  const handleModalFieldChange = (field: keyof typeof modalEditData, value: string) => {
    setModalEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Comments functionality
  const loadTaskComments = async (taskGid: string) => {
    setIsLoadingComments(true);
    try {
      const response = await getTaskStories(taskGid, orgId);
      if (response.success) {
        setTaskComments(response.stories || []);
      } else {
        console.error('Failed to load comments:', response.error);
        setTaskComments([]);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
      setTaskComments([]);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!selectedTaskForModal || !newComment.trim()) return;

    setIsAddingComment(true);
    try {
      const response = await addTaskStory(
        selectedTaskForModal.gid,
        newComment.trim(),
        orgId,
        false, // isPinned
        { alias: authenticatedUser?.userAlias, email: authenticatedUser?.email }
      );

      if (response.success && response.story) {
        // Add the new comment to the list
        setTaskComments(prev => [...prev, response.story!]);
        setNewComment(''); // Clear the input
        alert('Comment added successfully!');
      } else {
        alert(`Failed to add comment: ${response.error}`);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment. Please try again.');
    } finally {
      setIsAddingComment(false);
    }
  };

  // Load comments when modal opens
  useEffect(() => {
    if (selectedTaskForModal && !isEditingModal) {
      loadTaskComments(selectedTaskForModal.gid);
    }
  }, [selectedTaskForModal, isEditingModal]);

  // Clear comments when modal closes
  useEffect(() => {
    if (!selectedTaskForModal) {
      setTaskComments([]);
      setNewComment('');
    }
  }, [selectedTaskForModal]);

  // Auto-hide success/error messages after 5 seconds
  useEffect(() => {
    if (duplicateSuccess || duplicateError || deleteSuccess || deleteError) {
      const timer = setTimeout(() => {
        setDuplicateSuccess(null);
        setDuplicateError(null);
        setDeleteSuccess(null);
        setDeleteError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [duplicateSuccess, duplicateError, deleteSuccess, deleteError]);

  // Bulk OP Decision functionality
  const handleBulkOpDecision = async (decision: string) => {
    if (selectedTasks.size === 0) {
      alert(`Please select at least one task to mark as ${decision}`);
      return;
    }

    const newTaskChanges = { ...taskChanges };
    let hasNewChanges = false;

    selectedTasks.forEach(taskGid => {
      const task = tasks.find(t => t.gid === taskGid);
      if (task) {
        const currentOpDecision = (task as any).op_decision || '';
        if (currentOpDecision !== decision) {
          newTaskChanges[taskGid] = {
            ...newTaskChanges[taskGid],
            op_decision: decision
          };
          hasNewChanges = true;
        }
      }
    });

    if (hasNewChanges) {
      setTaskChanges(newTaskChanges);
      setHasUnsavedChanges(true);
     
    }

    // Clear selection after bulk action
    setSelectedTasks(new Set());
  };

  // Delete functionality
  const handleDeleteSelected = async () => {
    if (selectedTasks.size === 0) {
      setDeleteError('Please select at least one task to delete');
      return;
    }

    // Show confirmation dialog
    const selectedTasksData = tasks.filter(task => selectedTasks.has(task.gid));
    const taskNames = selectedTasksData.map(task => task.name).join('\n');
    const confirmMessage = `Are you sure you want to delete ${selectedTasks.size} task${selectedTasks.size > 1 ? 's' : ''}?\n\nThis action cannot be undone.\n\nTasks to delete:\n${taskNames}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);
    setDeleteSuccess(null);
    
    try {
      const taskGids = Array.from(selectedTasks);
      const result = await deleteTasks(taskGids, orgId);
      
      if (result.success) {
        setDeleteSuccess(`Successfully deleted ${selectedTasks.size} task${selectedTasks.size > 1 ? 's' : ''}`);
        // Refresh tasks to remove deleted tasks from the list
        await loadTasks(true);
        // Clear selection after successful deletion
        setSelectedTasks(new Set());
      } else {
        setDeleteError(result.error || 'Failed to delete tasks');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete tasks');
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderMultiSelectDropdown = (
    label: string, 
    field: keyof typeof filters, 
    options: string[]
  ) => {
    const selectedValues = filters[field] as string[];
    const isOpen = openDropdowns[field];
    
    return (
      <div 
        className="filter-dropdown" 
        ref={(el) => {
          dropdownRefs.current[field] = el;
        }}
      >
        <label>{label}:</label>
        <div className="dropdown-container">
          <button 
            className="dropdown-toggle"
            onClick={() => toggleDropdown(field)}
            type="button"
          >
            <span className="dropdown-text">
              {selectedValues.length === 0 
                ? `All ${label}` 
                : `${selectedValues.length} selected`}
            </span>
            <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
          </button>
          
          {isOpen && (
            <div className="dropdown-menu">
              <div className="dropdown-header">
                <button 
                  className="select-all-filter-btn"
                  onClick={() => setFilters({...filters, [field]: options})}
                  type="button"
                >
                  Select All
                </button>
                <button 
                  className="clear-filter-btn"
                  onClick={() => clearFilter(field)}
                  type="button"
                >
                  Clear All
                </button>
              </div>
              <div className="dropdown-options">
                {options.map(option => (
                  <label key={option} className="dropdown-option">
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(option)}
                      onChange={() => handleMultiSelectChange(field, option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Show error message if there's an organization error
  if (organizationError) {
    return (
      <div className="task-prioritization">
        <div className="task-header">
          <div className="task-header-content">
            <div>
              <h1>Roadmap Planner - Configuration Error</h1>
              <p>Unable to load tasks due to organization configuration issue</p>
            </div>
            <div className="header-buttons">
              <button onClick={() => navigate(orgId ? `/org/${orgId}/hub` : '/')} className="btn-back">
                ← Back to Hub
              </button>
            </div>
          </div>
        </div>
        <div className="error-container" style={{
          padding: '2rem',
          margin: '2rem',
          backgroundColor: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b'
        }}>
          <h3>Configuration Error</h3>
          <p>{organizationError}</p>
          <button 
            onClick={() => navigate(orgId ? `/org/${orgId}/settings` : '/')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Go to Portfolio Management
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div className="task-loading">Loading tasks...</div>;
  }

  return (
    <div className="task-prioritization">
      <div className="task-header">
        <div className="task-header-content">
          <div>
            <h1>Roadmap Planner{organizationName ? ` - ${organizationName}` : ''}</h1>
            <p>Select tasks and use AI to prioritize them{orgId ? ` for ${organizationName}` : ''}</p>
          </div>
          <div className="header-buttons">
            <button onClick={handleSync} disabled={isSyncing} className="btn-sync">
              {isSyncing ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid white',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '8px',
                    display: 'inline-block'
                  }}></div>
                  Syncing...
                </>
              ) : '🔄 Sync'}
            </button>
            <button onClick={() => navigate(`/admin/product/${orgId}${portfolioId ? `/${portfolioId}` : ''}/analytics`)} className="btn-analytics">
              📊 Analytics
            </button>
            {asanaProjectId && (
              <a
                href={`https://app.asana.com/0/${asanaProjectId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-sync"
                style={{ textDecoration: 'none' }}
                title="Open Asana project in a new tab"
              >
                📋 Open in Asana
              </a>
            )}
            <button onClick={() => {
              const url = `${window.location.origin}/org/${orgId}/roadmap/${portfolioId || ''}`;
              navigator.clipboard.writeText(url).then(() => {
                setDuplicateSuccess('Roadmap link copied to clipboard!');
              });
            }} className="btn-sync" title="Copy shareable roadmap link">
              🔗 Share Roadmap
            </button>
            <button onClick={() => navigate(orgId ? `/org/${orgId}/hub` : '/')} className="btn-back">
              ← Back to Hub
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <div className="tab-buttons">
          <button 
            className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTabChange('all')}
          >
            📋 All Requests
            <span className="tab-count">({tasks.length})</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'new' ? 'active' : ''}`}
            onClick={() => handleTabChange('new')}
          >
            🆕 New Requests
            <span className="tab-count">
              ({activeTab === 'new' ? filteredTasks.length : tasks.filter(newRequestsFilter).length})
            </span>
          </button>
          <button
            className={`tab-button ${activeTab === 'roadmap' ? 'active' : ''}`}
            onClick={() => handleTabChange('roadmap')}
          >
            📅 Stakeholder Roadmap
            <span className="tab-count">({tasks.length})</span>
          </button>
        </div>
        {activeTab !== 'roadmap' && <WorkflowBanner />}

        {activeTab === 'new' && (
          <div className="auto-filter-indicator">
            <span className="filter-badge">
              🔍 Auto-filtered: New, Review in Progress, or Unset decisions
            </span>
          </div>
        )}
      </div>

      {/* Stakeholder Roadmap Tab — rendered inline with tasks passed from this page */}
      {/* Filters — hidden when roadmap tab is active (roadmap has its own filter rail) */}
      {activeTab !== 'roadmap' && <div className="task-filters">
        <div className="filter-grid">
          <div className="filter-dropdown">
            <label>Name:</label>
            <input
              type="text"
              placeholder="Filter by name..."
              value={filters.name}
              onChange={(e) => setFilters({...filters, name: e.target.value})}
              className="name-filter-input"
            />
          </div>

          {renderMultiSelectDropdown('Program', 'program', 
            Array.from(new Set(tasks.map(t => (t as any).program_name || t.program || ''))).filter(Boolean).sort()
          )}
          {renderMultiSelectDropdown('Assignee', 'teamAssigned', 
            Array.from(new Set(tasks.map(task => task.assignee?.name || task.teamAssigned || ''))).filter(Boolean).sort()
          )}
          {renderMultiSelectDropdown(
            'L7 SDM', 
            'l7_sdm', 
            allL7Managers.map(manager => manager.name).sort()
          )}
          {renderMultiSelectDropdown(
            'Decision', 
            'op_decision', 
            [
              ...Array.from(new Set(tasks.map(task => (task as any).op_decision))).filter(Boolean).sort(),
              'Unset'
            ]
          )}
          {renderMultiSelectDropdown(
            'Target Year', 
            'target_year', 
            [
              ...Array.from(new Set(tasks.map(task => (task as any).target_year))).filter(Boolean).sort(),
              'Unset'
            ]
          )}
          {renderMultiSelectDropdown(
            'Request Type', 
            'request_type', 
            [
              ...Array.from(new Set(tasks.map(task => (task as any).request_type))).filter(Boolean).sort(),
              'Unset'
            ]
          )}
          {renderMultiSelectDropdown(
            'Status',
            'status',
            [
              ...Array.from(new Set(tasks.map(task => (task as any).status))).filter(Boolean).sort(),
              'Unset'
            ]
          )}
        </div>
      </div>}

      {activeTab === 'roadmap' && (
        <StakeholderRoadmap
          tasks={filteredTasks.map(task => {
            const changes = taskChanges[task.gid];
            return changes ? { ...task, ...changes } : task;
          })}
          isEmbedded={true}
        />
      )}

      {activeTab !== 'roadmap' && (<>
      {/* Dashboard Metrics */}
      <div className="dashboard-metrics">
        <div className="metric-card">
          <div className="metric-label">Year</div>
          <div className="metric-value">
            {filters.target_year.length === 0 
              ? 'All Years' 
              : filters.target_year.length === 1 
                ? filters.target_year[0] 
                : `${filters.target_year.length} Selected`
            }
          </div>
        </div>
              <div className="metric-card">
          <div className="metric-label">Total Requests</div>
          <div className="metric-value">{dashboardMetrics.totalTasks}</div>
        </div>
  <div className="metric-card">
          <div className="metric-label">Pending Requests</div>
          <div className="metric-value">{dashboardMetrics.tbdTasks}</div>
        </div>
        <div className="metric-card metric-primary">
          <div className="metric-label-p1">Total Annualized Entitlement</div>
          <div className="metric-value">{formatCompactCurrency(dashboardMetrics.totalEntitlement)}</div>
        </div>
       
        <div className="metric-card">
          <div className="metric-label">Target SDE HC</div>
          <div className="metric-value">{dashboardMetrics.targetSDE}</div>
        </div>
        
        <div className="metric-card">
          <div className="metric-label">Non Discretionary</div>
          
          <div className="metric-value">{formatNumber(dashboardMetrics.rtbHC)}</div>
         <div className="metric-subtitle">
         KTLO HC
          </div>
          <div className="metric-subtitle">
            ({dashboardMetrics.targetSDE > 0 ? Math.round((dashboardMetrics.rtbHC / dashboardMetrics.targetSDE) * 100) : 0}%)
          </div>
        </div>
        
  
        
       
        
        <div className="metric-card">
          <div className="metric-label">Discretionary</div>
         
          <div className="metric-value">{formatNumber(dashboardMetrics.totalHcSde)}</div>
            <div className="metric-subtitle">
         Prioritized HC
          </div>
              <div className="metric-subtitle">
            ({dashboardMetrics.targetSDE  > 0 ? Math.round((dashboardMetrics.totalHcSde / dashboardMetrics.targetSDE) * 100) : 0}%)
          </div>
        </div>

         <div className="metric-card">
          <div className="metric-label">Total Planned</div>
          <div className="metric-value">{Math.round(dashboardMetrics.totalHcSde + dashboardMetrics.rtbHC)}</div>
               <div className="metric-subtitle">
          KTLO+Discretionary
          </div>
              <div className="metric-subtitle">
            ({dashboardMetrics.targetSDE  > 0 ? Math.round(((dashboardMetrics.totalHcSde + dashboardMetrics.rtbHC) / dashboardMetrics.targetSDE) * 100) : 0}%)
          </div>
        </div>
 
        <div className="metric-card metric-p1">
          <div className="metric-label-p1">P1 ATL HC</div>
          <div className="metric-value">{formatNumber(dashboardMetrics.p1OpPriority)}</div>
        </div>
        <div className="metric-card metric-p2">
          <div className="metric-label-p1">P2 ATL HC</div>
          <div className="metric-value">{formatNumber(dashboardMetrics.p2OpPriority)}</div>
        </div>
        <div className="metric-card metric-btl">
          <div className="metric-label-p1">BTL HC</div>
          <div className="metric-value">{formatNumber(dashboardMetrics.BTLOpPriority)}</div>
        </div>
      

      </div>

      {/* Actions */}
      <div className="task-actions">
        <div className="groupby-selector">
          <label htmlFor="groupby-dropdown">Group By:</label>
          <select
            id="groupby-dropdown"
            value={groupByField}
            onChange={(e) => handleGroupByChange(e.target.value as 'none' | 'op_decision' | 'program' | 'l7_manager' | 'impact_metrics')}
            className="groupby-dropdown"
          >
            <option value="none">None (Individual Tasks)</option>
            <option value="op_decision">OP Decision</option>
            <option value="program">Program</option>
            <option value="l7_manager">L7 Sr Manager</option>
            <option value="impact_metrics">Impact</option>
          </select>
        </div>
        
        <button onClick={handleSelectAll} className="btn-select-all">
          {selectedTasks.size === filteredTasks.length ? 'Deselect All' : 'Select All'}
        </button>
        
        {/* Bulk Decision Change dropdown - only show when tasks are selected */}
        {selectedTasks.size > 0 && (
          <>
            <div className="bulk-decision-selector">
              <label htmlFor="bulk-decision-dropdown">Set Decision for {selectedTasks.size} selected:</label>
              <select
                id="bulk-decision-dropdown"
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkOpDecision(e.target.value);
                    e.target.value = ''; // Reset dropdown after selection
                  }
                }}
                className="bulk-decision-dropdown"
                defaultValue=""
              >
                <option value="" disabled>Choose decision...</option>
                <option value="Review in Progress">📝 Review in Progress</option>
                <option value="New">🆕 New</option>
                <option value="P1 - ATL">🔴 P1 - ATL</option>
                <option value="P2 - ATL">🟡 P2 - ATL</option>
                <option value="P3 - BTL">🔵 P3 - BTL</option>
                <option value="P4 - BTL">⚫ P4 - BTL</option>
              </select>
            </div>
            {/* <button
              onClick={handleDuplicateSelected}
              disabled={duplicateLoading}
              className="btn-duplicate-bulk"
              title="Duplicate selected tasks to current organization"
            >
              {duplicateLoading ? (
                <>
                  <div className="loading-spinner"></div>
                  Duplicating...
                </>
              ) : (
                <>
                  📋 Duplicate ({selectedTasks.size})
                </>
              )}
            </button> */}
            <button
              onClick={handleDuplicateSelectedToAnotherOrg}
              disabled={duplicateLoading}
              className="btn-duplicate-org-bulk"
              title="Duplicate selected tasks to another organization"
            >
              📋➡️ Duplicate to Org ({selectedTasks.size})
            </button>
            <button
              onClick={handleDeleteSelected}
              disabled={deleteLoading}
              className="btn-delete-bulk"
              title="Delete selected tasks permanently"
            >
              {deleteLoading ? (
                <>
                  <div className="loading-spinner"></div>
                  Deleting...
                </>
              ) : (
                <>
                  🗑️ Delete ({selectedTasks.size})
                </>
              )}
            </button>
          </>
        )}
        <button 
          onClick={handlePrioritize} 
          disabled={selectedTasks.size === 0 || isPrioritizing}
          className="btn-prioritize"
        >
          {isPrioritizing ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid white',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: '8px',
                display: 'inline-block'
              }}></div>
              Prioritizing...
            </>
          ) : `Prioritize (${selectedTasks.size})`}
        </button>
        <button 
          onClick={handleGenerateNarrative} 
          disabled={selectedTasks.size === 0 || isGeneratingNarrative}
          className="btn-narrative"
        >
          {isGeneratingNarrative ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid white',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: '8px',
                display: 'inline-block'
              }}></div>
              Generating...
            </>
          ) : `📖 Generate Narrative (${selectedTasks.size})`}
        </button>
        {hasUnsavedChanges && (
          <>
            <button 
              onClick={handleSaveChanges}
              disabled={isSaving}
              className="btn-save-changes"
            >
              {isSaving ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid white',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '8px',
                    display: 'inline-block'
                  }}></div>
                  Saving...
                </>
              ) : `Save Changes (${Object.keys(taskChanges).length})`}
            </button>
            <button 
              onClick={() => {
                if (window.confirm('Are you sure you want to discard all unsaved changes?')) {
                  setTaskChanges({});
                  setHasUnsavedChanges(false);
                }
              }}
              disabled={isSaving}
              className="btn-clear-changes"
            >
              Clear Changes
            </button>
          </>
        )}
        <div className="task-count">
          Showing {filteredTasks.length} of {tasks.length} tasks
        </div>
      </div>

      {/* Tasks Table */}
      <div className="task-table-container">
        <table className="task-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedTasks.size === filteredTasks.length && filteredTasks.length > 0}
                  onChange={handleSelectAll}
                  title={selectedTasks.size === filteredTasks.length ? 'Deselect All' : 'Select All'}
                />
              </th>
              <th 
                className={`sortable-header ${sortConfig.key === 'op_decision' ? 'sorted' : ''}`}
                onClick={() => handleSort('op_decision')}
              >
                <div className="sortable-header-content">
                  <span className="sortable-header-text">Decision</span>
                  {getSortIcon('op_decision')}
                </div>
              </th>
              <th>Name</th>
              <th 
                className={`sortable-header ${sortConfig.key === 'program' ? 'sorted' : ''}`}
                onClick={() => handleSort('program')}
              >
                <div className="sortable-header-content">
                  <span className="sortable-header-text">Program</span>
                  {getSortIcon('program')}
                </div>
              </th>
              <th 
                className={`sortable-header ${sortConfig.key === 'teamAssigned' ? 'sorted' : ''}`}
                onClick={() => handleSort('teamAssigned')}
              >
                <div className="sortable-header-content">
                  <span className="sortable-header-text">Assignee</span>
                  {getSortIcon('teamAssigned')}
                </div>
              </th>
              <th 
                className={`sortable-header ${sortConfig.key === 'l7_manager' ? 'sorted' : ''}`}
                onClick={() => handleSort('l7_manager')}
              >
                <div className="sortable-header-content">
                  <span className="sortable-header-text">L7 SDM</span>
                  {getSortIcon('l7_manager')}
                </div>
              </th>
              <th 
                className={`sortable-header ${sortConfig.key === 'annualized_entitlement' ? 'sorted' : ''}`}
                onClick={() => handleSort('annualized_entitlement')}
              >
                <div className="sortable-header-content">
                  <span className="sortable-header-text">Entitlement</span>
                  {getSortIcon('annualized_entitlement')}
                </div>
              </th>
              <th style={{ minWidth: '80px', width: '80px' }}>SDE HC</th>
              <th>Status</th>
              <th>Launch Date</th>
              <th 
                className={`sortable-header ${sortConfig.key === 'created_at' ? 'sorted' : ''}`}
                onClick={() => handleSort('created_at')}
              >
                <div className="sortable-header-content">
                  <span className="sortable-header-text">Date Created</span>
                  {getSortIcon('created_at')}
                </div>
              </th>
              <th>Rationale</th>
            </tr>
          </thead>
          <tbody>
            {groupByField === 'none' ? (
              // Individual task view
              getSortedTasks(filteredTasks).map(task => {
                return (
                  <tr key={task.gid} className={selectedTasks.has(task.gid) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedTasks.has(task.gid)}
                        onChange={() => handleTaskSelection(task.gid)}
                      />
                    </td>
                    <td>
                      {renderEditableDropdown(task, 'op_decision' as keyof AsanaTask, 
                        enumConfig?.op_decision ? Object.keys(enumConfig.op_decision.options) : 
                        Array.from(new Set(tasks.map(task => (task as any).op_decision))).filter(Boolean).sort()
                      )}
                    </td>
                    <td className="name-cell" style={{ minWidth: '200px' }}>
                      <div 
                        className="name-preview"
                        onClick={() => setSelectedTaskForModal(task)}
                        title="Click to view task details"
                      >
                        {task.name || (
                          <span className="no-name">
                            Click to view details
                            <span className="expand-icon">🔍</span>
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {renderEditableDropdown(task, 'program', 
                        enumConfig?.program_name ? Object.keys(enumConfig.program_name.options) :
                        Array.from(new Set(tasks.map(t => (t as any).program_name || t.program || ''))).filter(Boolean).sort()
                      )}
                    </td>
                    <td>
                      {renderEditableDropdown(task, 'teamAssigned', 
                        Array.from(new Set(tasks.map(task => task.assignee?.name || task.teamAssigned || ''))).filter(Boolean).sort()
                      )}
                    </td>
                    <td className="l7-manager-cell">
                      {renderEditableDropdown(task, 'l7_manager', 
                        allL7Managers.map(manager => manager.name).sort()
                      )}
                    </td>
                    <td className="numeric-cell">
                      {renderEditableNumber(task, 'annualized_entitlement')}
                    </td>
                    <td className="numeric-cell" style={{ minWidth: '80px', width: '80px' }}>
                      {renderEditableNumber(task, 'hc_sde')}
                    </td>
                    <td style={{ minWidth: '160px', width: '160px' }}>
                      {renderEditableDropdown(task, 'status',
                        enumConfig?.status ? Object.keys(enumConfig.status.options) :
                        Array.from(new Set(tasks.map(t => (t as any).status))).filter(Boolean).sort()
                      )}
                    </td>
                    <td>
                      {renderEditableDatePicker(task, 'launch_date')}
                    </td>
                    <td className="date-cell">
                      {formatDate(task.created_at)}
                    </td>
                    <td className="rationale-cell">
                      {getCurrentFieldValue(task, 'rationale') ? (
                        <div className="rationale-content">
                          <span className="rationale-icon">🤖</span>
                          <span className="rationale-text">{getCurrentFieldValue(task, 'rationale')}</span>
                        </div>
                      ) : (
                        <span className="no-rationale">-</span>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              // Grouped view
              getGroupedData(filteredTasks, groupByField)?.map(group => (
                <React.Fragment key={group.name}>
                  {/* Group Header Row */}
                  <tr 
                    className="group-header-row" 
                    onClick={() => toggleGroup(group.name)}
                  >
                    <td colSpan={2}>
                      <div className="group-header-content">
                        <span className="expand-icon">
                          {expandedGroups.has(group.name) ? '🔽' : '▶️'}
                        </span>
                        <strong className="group-name">{group.name}</strong>
                        <span className="task-count-badge">({group.taskCount} tasks)</span>
                      </div>
                    </td>
                    <td colSpan={6} className="group-totals">
                      <div className="group-totals-content">
                        <span className="total-entitlement">
                          💰 {formatCompactCurrency(group.totalEntitlement)}
                        </span>
                        <span className="total-sde">
                          👥 {formatNumber(group.totalSDE)} SDE
                        </span>
                      </div>
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                  
                  {/* Individual Task Rows (when expanded) */}
                  {expandedGroups.has(group.name) && 
                    getSortedTasks(group.tasks).map(task => (
                      <tr key={task.gid} className={`grouped-task ${selectedTasks.has(task.gid) ? 'selected' : ''}`}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedTasks.has(task.gid)}
                            onChange={() => handleTaskSelection(task.gid)}
                          />
                        </td>
                        <td>
                          {renderEditableDropdown(task, 'op_decision' as keyof AsanaTask, 
                            enumConfig?.op_decision ? Object.keys(enumConfig.op_decision.options) : 
                            Array.from(new Set(tasks.map(task => (task as any).op_decision))).filter(Boolean).sort()
                          )}
                        </td>
                        <td className="name-cell" style={{ minWidth: '200px' }}>
                          <div 
                            className="name-preview"
                            onClick={() => setSelectedTaskForModal(task)}
                            title="Click to view task details"
                          >
                            {task.name || (
                              <span className="no-name">
                                Click to view details
                                <span className="expand-icon">🔍</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {renderEditableDropdown(task, 'program', 
                            enumConfig?.program_name ? Object.keys(enumConfig.program_name.options) :
                            Array.from(new Set(tasks.map(t => (t as any).program_name || t.program || ''))).filter(Boolean).sort()
                          )}
                        </td>
                        <td>
                          {renderEditableDropdown(task, 'teamAssigned', 
                            Array.from(new Set(tasks.map(task => task.assignee?.name || task.teamAssigned || ''))).filter(Boolean).sort()
                          )}
                        </td>
                        <td className="l7-manager-cell">
                          {renderEditableDropdown(task, 'l7_manager', 
                            allL7Managers.map(manager => manager.name).sort()
                          )}
                        </td>
                        <td className="numeric-cell">
                          {renderEditableNumber(task, 'annualized_entitlement')}
                        </td>
                        <td className="numeric-cell" style={{ minWidth: '80px', width: '80px' }}>
                          {renderEditableNumber(task, 'hc_sde')}
                        </td>
                        <td style={{ minWidth: '160px', width: '160px' }}>
                          {renderEditableDropdown(task, 'status',
                            enumConfig?.status ? Object.keys(enumConfig.status.options) :
                            Array.from(new Set(tasks.map(t => (t as any).status))).filter(Boolean).sort()
                          )}
                        </td>
                        <td>
                          {renderEditableDatePicker(task, 'launch_date')}
                        </td>
                        <td className="date-cell">
                          {formatDate(task.created_at)}
                        </td>
                        <td className="rationale-cell">
                          {getCurrentFieldValue(task, 'rationale') ? (
                            <div className="rationale-content">
                              <span className="rationale-icon">🤖</span>
                              <span className="rationale-text">{getCurrentFieldValue(task, 'rationale')}</span>
                            </div>
                          ) : (
                            <span className="no-rationale">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  }
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
      </>)}

      {/* Prioritization Results */}
      {prioritizationResults.length > 0 && (
        <div className="prioritization-results">
          <h3>Prioritization Results</h3>
          <div className="results-grid">
            {prioritizationResults.map(result => {
              const task = tasks.find(t => t.gid === result.Gid);
              return (
                <div key={result.Gid} className="result-card">
                  <div className="result-header">
                    <h4>{task?.name || 'Unknown Task'}</h4>
                    <div className="result-ids">
                      <span className="task-gid">GID: {result.Gid}</span>
                      {(task as any)?.aft && (
                        <span className="task-aft">AFT: {(task as any).aft}</span>
                      )}
                    </div>
                  </div>
                  <div className="result-badges">
                    {result.op_decision && (
                      <span className={`priority-badge op-decision-badge ${
                        result.op_decision.includes('P1') ? 'priority-high' : 
                        result.op_decision.includes('P2') ? 'priority-medium' : 
                        'priority-low'
                      }`}>
                        {result.op_decision}
                      </span>
                    )}
                    {result.mission_fit && (
                      <span className={`priority-badge mission-fit-badge ${
                        result.mission_fit === 'Aligned with Mission' ? 'mission-aligned' : 
                        result.mission_fit === 'Not Aligned' ? 'mission-not-aligned' : 
                        'mission-other'
                      }`}>
                        {result.mission_fit}
                      </span>
                    )}
                  </div>
                  <p>{result.notes}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {(duplicateSuccess || duplicateError || deleteSuccess || deleteError) && (
        <div className={`duplicate-notification ${(duplicateSuccess || deleteSuccess) ? 'success' : 'error'}`}>
          <div className="notification-content">
            <span className="notification-icon">
              {(duplicateSuccess || deleteSuccess) ? '✅' : '❌'}
            </span>
            <span className="notification-message">
              {duplicateSuccess || duplicateError || deleteSuccess || deleteError}
            </span>
            <button 
              className="notification-close"
              onClick={() => {
                setDuplicateSuccess(null);
                setDuplicateError(null);
                setDeleteSuccess(null);
                setDeleteError(null);
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Duplicate to Another Org Modal */}
      {showDuplicateModal && taskToDuplicate && (
        <div className="modal-overlay" onClick={closeDuplicateModal}>
          <div className="modal-content duplicate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Duplicate Task to Another Organization</h3>
              <button 
                className="modal-close"
                onClick={closeDuplicateModal}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="duplicate-task-info">
                <h4>Task to Duplicate:</h4>
                <p><strong>{taskToDuplicate.name}</strong></p>
                <p className="task-gid">GID: {taskToDuplicate.gid}</p>
              </div>
              
              <div className="organization-selection">
                <h4>Select Target Portfolio:</h4>
                <select
                  value={selectedTargetOrg}
                  onChange={(e) => setSelectedTargetOrg(e.target.value)}
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">-- Select Portfolio --</option>
                  {availableOrganizations.flatMap((org: any) => {
                    const portfolios = org.portfolios as any[] | undefined;
                    if (portfolios && portfolios.length > 0) {
                      return portfolios.map((p: any) => (
                        <option key={p.asanaProjectId} value={p.asanaProjectId}>
                          {org.name} → {p.name}
                        </option>
                      ));
                    }
                    if (org.asanaProject) {
                      return [<option key={org.id} value={org.asanaProject}>{org.name}</option>];
                    }
                    return [];
                  })}
                </select>
              </div>
              
              {duplicateError && (
                <div className="error-message">
                  <span className="error-icon">❌</span>
                  {duplicateError}
                </div>
              )}
              
              <div className="modal-actions">
                <button
                  onClick={confirmDuplicateToAnotherOrg}
                  disabled={!selectedTargetOrg || duplicateLoading}
                  className="btn-confirm-duplicate"
                >
                  {duplicateLoading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Duplicating...
                    </>
                  ) : (
                    '📋 Duplicate Task'
                  )}
                </button>
                <button
                  onClick={closeDuplicateModal}
                  disabled={duplicateLoading}
                  className="btn-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Description Modal - Redesigned */}
      {selectedTaskForModal && (
        <div className="modal-overlay" onClick={() => setSelectedTaskForModal(null)}>
          <div className="modal-content task-detail-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header with Status Badges */}
            <div className="modal-header task-detail-header">
              <div className="task-header-content">
                <div className="task-title-section">
                  <h3 className="task-title">{selectedTaskForModal.name}</h3>
                  <div className="task-id-badge">ID: {selectedTaskForModal.gid}</div>
                </div>
                <div className="task-status-badges">
                  {(selectedTaskForModal as any).op_decision && (
                    <span className={`status-badge priority-badge ${
                      (selectedTaskForModal as any).op_decision.includes('P1') ? 'badge-p1' : 
                      (selectedTaskForModal as any).op_decision.includes('P2') ? 'badge-p2' : 
                      'badge-other'
                    }`}>
                      {(selectedTaskForModal as any).op_decision}
                    </span>
                  )}
                  {/* {(selectedTaskForModal as any).mission_fit && (
                    <span className={`status-badge mission-badge ${
                      (selectedTaskForModal as any).mission_fit === 'Aligned with Mission' ? 'badge-aligned' : 
                      (selectedTaskForModal as any).mission_fit === 'Not Aligned' ? 'badge-not-aligned' : 
                      'badge-neutral'
                    }`}>
                      {(selectedTaskForModal as any).mission_fit}
                    </span>
                  )} */}
                  {(selectedTaskForModal as any).impact_metrics && (selectedTaskForModal as any).impact_metrics !== 'Not specified' && (
                    <span className="status-badge impact-badge">
                      📊 {(selectedTaskForModal as any).impact_metrics}
                    </span>
                  )}

  <span className="status-badge impact-badge">
            {(selectedTaskForModal as any).target_year}
                    </span>

                
                </div>
              </div>
              <div className="modal-header-actions">
                {!isEditingModal && (
                  <button 
                    className="btn btn-primary btn-small"
                    onClick={handleEditModalTask}
                    disabled={isSavingModal}
                  >
                    ✏️ Edit
                  </button>
                )}
                <button 
                  className="modal-close"
                  onClick={() => {
                    setSelectedTaskForModal(null);
                    setIsEditingModal(false);
                  }}
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="modal-body task-detail-body">
              {isEditingModal ? (
                /* Edit Form */
                <div className="task-edit-form">
                  <div className="edit-form-section">
                    <div className="form-group">
                      <label>Task Name</label>
                      <input
                        type="text"
                        value={modalEditData.name}
                        onChange={(e) => handleModalFieldChange('name', e.target.value)}
                        className="form-input"
                        placeholder="Enter task name..."
                      />
                    </div>

                    <div className="form-group">
                      <label>Problem Statement</label>
                      <textarea
                        value={modalEditData.problem_statement}
                        onChange={(e) => handleModalFieldChange('problem_statement', e.target.value)}
                        className="form-textarea"
                        rows={4}
                        placeholder="Describe the problem this task addresses..."
                      />
                    </div>

                    <div className="form-group">
                      <label>Desired Outcome</label>
                      <textarea
                        value={modalEditData.desired_outcome}
                        onChange={(e) => handleModalFieldChange('desired_outcome', e.target.value)}
                        className="form-textarea"
                        rows={4}
                        placeholder="Describe the desired outcome..."
                      />
                    </div>

                    <div className="form-group">
                      <label>Business Impact</label>
                      <textarea
                        value={modalEditData.business_impact}
                        onChange={(e) => handleModalFieldChange('business_impact', e.target.value)}
                        className="form-textarea"
                        rows={4}
                        placeholder="Describe the business impact..."
                      />
                    </div>

                    <div className="form-group">
                      <label>Decision Rationale</label>
                      <textarea
                        value={modalEditData.rationale}
                        onChange={(e) => handleModalFieldChange('rationale', e.target.value)}
                        className="form-textarea"
                        rows={4}
                        placeholder="Explain the decision rationale..."
                      />
                    </div>

                    <div className="form-group">
                      <label>Theme</label>
                      <select
                        value={modalEditData.theme}
                        onChange={(e) => handleModalFieldChange('theme', e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select theme...</option>
                        {(enumConfig?.theme ? Object.keys(enumConfig.theme.options) : getUniqueValues('theme')).map(theme => (
                          <option key={theme} value={theme}>{theme}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Impact Metric</label>
                      <select
                        value={modalEditData.impact_metrics}
                        onChange={(e) => handleModalFieldChange('impact_metrics', e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select impact metric...</option>
                        {(enumConfig?.impact_metrics ? Object.keys(enumConfig.impact_metrics.options) : 
                          Array.from(new Set(tasks.map(task => (task as any).impact_metrics))).filter(Boolean).sort()
                        ).map(metric => (
                          <option key={metric} value={metric}>{metric}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Target Year</label>
                      <select
                        value={modalEditData.target_year}
                        onChange={(e) => handleModalFieldChange('target_year', e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select target year...</option>
                        {(() => {
                          // Use enum config options if available, otherwise fall back to task data + current/next year
                          if (enumConfig?.target_year && Object.keys(enumConfig.target_year.options).length > 0) {
                            return Object.keys(enumConfig.target_year.options).sort().map(year => (
                              <option key={year} value={year}>{year}</option>
                            ));
                          }
                          const currentYear = new Date().getFullYear();
                          const taskYears = Array.from(new Set(tasks.map(task => (task as any).target_year))).filter(Boolean);
                          const allYears = new Set([
                            ...taskYears,
                            currentYear.toString(),
                            (currentYear + 1).toString()
                          ]);
                          return Array.from(allYears).sort().map(year => (
                            <option key={year} value={year}>{year}</option>
                          ));
                        })()}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Request Type</label>
                      <select
                        value={modalEditData.request_type}
                        onChange={(e) => handleModalFieldChange('request_type', e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select request type...</option>
                        {(enumConfig?.request_type ? Object.keys(enumConfig.request_type.options) : 
                          Array.from(new Set(tasks.map(task => (task as any).request_type))).filter(Boolean).sort()
                        ).map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Launch Date</label>
                      <input
                        type="date"
                        value={modalEditData.launch_date || ''}
                        onChange={(e) => handleModalFieldChange('launch_date', e.target.value)}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="edit-form-actions">
                    <button
                      onClick={handleSaveModalEdit}
                      disabled={isSavingModal}
                      className="btn btn-primary"
                    >
                      {isSavingModal ? (
                        <>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid white',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            marginRight: '8px',
                            display: 'inline-block'
                          }}></div>
                          Saving...
                        </>
                      ) : (
                        '💾 Save Changes'
                      )}
                    </button>
                    <button
                      onClick={handleCancelModalEdit}
                      disabled={isSavingModal}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <>
                  {/* Key Metrics Cards */}
                  <div className="task-metrics-section">
                    <div className="metrics-cards">
                      <div className="metric-card-small metric-entitlement">
                        <div className="metric-icon">💰</div>
                        <div className="metric-content">
                          <div className="metric-label">Entitlement</div>
                          <div className="metric-value">
                            {(selectedTaskForModal as any).annualized_entitlement 
                              ? formatCurrency(parseFloat((selectedTaskForModal as any).annualized_entitlement))
                              : 'Not specified'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="metric-card-small metric-hc">
                        <div className="metric-icon">👥</div>
                        <div className="metric-content">
                          <div className="metric-label">SDE HC</div>
                          <div className="metric-value">
                            {(selectedTaskForModal as any).hc_sde 
                              ? formatNumber(parseFloat((selectedTaskForModal as any).hc_sde))
                              : 'Not specified'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="metric-card-small metric-efficiency">
                        <div className="metric-icon">⚡</div>
                        <div className="metric-content">
                          <div className="metric-label">Efficiency</div>
                          <div className="metric-value">
                            {(selectedTaskForModal as any).hc_sde && (selectedTaskForModal as any).annualized_entitlement
                              ? formatCompactCurrency(parseFloat((selectedTaskForModal as any).annualized_entitlement) / parseFloat((selectedTaskForModal as any).hc_sde))
                              : 'N/A'}/SDE
                          </div>
                        </div>
                      </div>
                      
                      <div className="metric-card-small metric-date">
                        <div className="metric-icon">📅</div>
                        <div className="metric-content">
                          <div className="metric-label">Created</div>
                          <div className="metric-value">{formatDate(selectedTaskForModal.created_at)}</div>
                        </div>
                      </div>
                          <div className="metric-card-small metric-date">
                        <div className="metric-icon">📊 </div>
                        <div className="metric-content">
                          <div className="metric-label">Impact Metric</div>
                          <div className="metric-value">{(selectedTaskForModal as any).impact_metrics || 'Not specified'}</div>
                        </div>
                      </div>  


                   

                              </div>     
                  </div>

                  {/* Main Content Sections */}
                  <div className="task-content-sections">
                    {/* Problem & Solution Section */}
                    <div className="content-section-group">
                      {selectedTaskForModal.problem_statement && (
                        <div className="task-detail-card">
                          <div className="card-header">
                            <div className="card-icon">🎯</div>
                            <h4>Problem Statement</h4>
                          </div>
                          <div className="card-content">
                            <p className="task-description">{selectedTaskForModal.problem_statement}</p>
                          </div>
                        </div>
                      )}
                      
                      {selectedTaskForModal.desired_outcome && (
                        <div className="task-detail-card">
                          <div className="card-header">
                            <div className="card-icon">🎯</div>
                            <h4>Desired Outcome</h4>
                          </div>
                          <div className="card-content">
                            <p className="task-description">{selectedTaskForModal.desired_outcome}</p>
                          </div>
                        </div>
                      )}
                      
                      {selectedTaskForModal.business_impact && (
                        <div className="task-detail-card">
                          <div className="card-header">
                            <div className="card-icon">📈</div>
                            <h4>Business Impact</h4>
                          </div>
                          <div className="card-content">
                            <p className="task-description">{selectedTaskForModal.business_impact}</p>
                          </div>
                        </div>
                      )}

    


                    </div>

                    {/* Assignment & Classification Section */}
                    <div className="task-assignment-section">
                      <h4 className="section-title">📋 Assignment & Classification</h4>
                      <div className="assignment-grid">
                        <div className="assignment-item">
                          <div className="assignment-label">Program</div>
                          <div className="assignment-value program-tag">
                            {selectedTaskForModal.program || 'Not specified'}
                          </div>
                        </div>
                        
                        <div className="assignment-item">
                          <div className="assignment-label">Assignee</div>
                          <div className="assignment-value assignee-tag">
                            👤 {selectedTaskForModal.assignee?.name || selectedTaskForModal.teamAssigned || 'Not assigned'}
                          </div>
                        </div>
                        
                        <div className="assignment-item">
                          <div className="assignment-label">L7 SDM</div>
                          <div className="assignment-value l7-tag">
                            👨‍💼 {(selectedTaskForModal as any).l7_sdm || (selectedTaskForModal as any).l7_manager || 'Not specified'}
                          </div>
                        </div>

                        <div className="assignment-item">
                          <div className="assignment-label">Status</div>
                          <div className="assignment-value">
                            🔖 {(selectedTaskForModal as any).status || 'Not specified'}
                          </div>
                        </div>

                        <div className="assignment-item">
                          <div className="assignment-label">Launch Date</div>
                          <div className="assignment-value">
                            📅 {getCurrentFieldValue(selectedTaskForModal, 'launch_date')
                              ? formatDate(getCurrentFieldValue(selectedTaskForModal, 'launch_date') as string)
                              : 'Not specified'}
                          </div>
                        </div>

                        {(selectedTaskForModal as any).key_deliverables && (
                          <div className="assignment-item" style={{ gridColumn: '1 / -1' }}>
                            <div className="assignment-label">Key Deliverables</div>
                            <div className="assignment-value" style={{ whiteSpace: 'pre-wrap' }}>
                              📦 {(selectedTaskForModal as any).key_deliverables}
                            </div>
                          </div>
                        )}

                        {(selectedTaskForModal as any).aft && (
                          <div className="assignment-item">
                            <div className="assignment-label">AFT Ticket</div>
                            <div className="assignment-value aft-tag">
                              🎫 {(selectedTaskForModal as any).aft}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* AI Rationale Section */}
                    {selectedTaskForModal.rationale && (
                      <div className="task-detail-card ai-rationale-card">
                        <div className="card-header">
                          <div className="card-icon">🤖</div>
                          <h4>Decision Rationale</h4>
                        </div>
                        <div className="card-content">
                          <div className="ai-rationale-content">
                            <p>{selectedTaskForModal.rationale}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Additional Information */}
                    {selectedTaskForModal.stakeholders && (
                      <div className="task-detail-card">
                        <div className="card-header">
                          <div className="card-icon">👥</div>
                          <h4>Stakeholders</h4>
                        </div>
                        <div className="card-content">
                          <p className="task-description">{selectedTaskForModal.stakeholders}</p>
                        </div>
                      </div>
                    )}

                    {/* Comments Section */}
                    <div className="task-detail-card comments-card">
                      <div className="card-header">
                        <div className="card-icon">💬</div>
                        <h4>Comments ({taskComments.length})</h4>
                      </div>
                      <div className="card-content">
                        {/* Add Comment Form - Moved to top */}
                        <div className="add-comment-form" style={{
                          marginBottom: '16px',
                          padding: '16px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div className="comment-input-group">
                            <textarea
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder="Add a comment..."
                              className="comment-textarea"
                              rows={3}
                              disabled={isAddingComment}
                              style={{
                                width: '100%',
                                padding: '12px',
                                border: '1px solid #ced4da',
                                borderRadius: '6px',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                resize: 'vertical',
                                minHeight: '80px',
                                marginBottom: '12px',
                                fontFamily: 'inherit'
                              }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <button
                                onClick={handleAddComment}
                                disabled={!newComment.trim() || isAddingComment}
                                className="add-comment-btn"
                                style={{
                                  backgroundColor: !newComment.trim() || isAddingComment ? '#6c757d' : '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  cursor: !newComment.trim() || isAddingComment ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'background-color 0.2s ease'
                                }}
                              >
                                {isAddingComment ? (
                                  <>
                                    <div style={{
                                      width: '14px',
                                      height: '14px',
                                      border: '2px solid white',
                                      borderTop: '2px solid transparent',
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite'
                                    }}></div>
                                    Adding...
                                  </>
                                ) : (
                                  <>
                                    💬 Add Comment
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Comments List */}
                        <div className="comments-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {isLoadingComments ? (
                            <div className="comments-loading" style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              padding: '20px',
                              color: '#666'
                            }}>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                border: '2px solid #ddd',
                                borderTop: '2px solid #007bff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                marginRight: '10px'
                              }}></div>
                              <span>Loading comments...</span>
                            </div>
                          ) : taskComments.length > 0 ? (
                            taskComments
                              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                              .map((comment, index) => (
                                <div 
                                  key={comment.gid} 
                                  className="comment-item"
                                  style={{
                                    backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff',
                                    borderLeft: comment.is_pinned ? '4px solid #ffc107' : '4px solid #007bff',
                                    padding: '16px',
                                    marginBottom: '8px',
                                    borderRadius: '8px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <div className="comment-header" style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                    paddingBottom: '8px',
                                    borderBottom: '1px solid #e9ecef'
                                  }}>
                                    <div className="comment-author" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <a 
                                        href={`mailto:${comment.created_by.email}`}
                                        className="author-name"
                                        style={{
                                          color: '#007bff',
                                          textDecoration: 'none',
                                          fontWeight: '600',
                                          fontSize: '14px'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                        onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                                      >
                                        {comment.created_by.name}
                                      </a>
                                      {comment.is_pinned && (
                                        <span 
                                          className="pinned-badge"
                                          style={{
                                            backgroundColor: '#ffc107',
                                            color: '#212529',
                                            padding: '2px 6px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: '500'
                                          }}
                                        >
                                          📌 Pinned
                                        </span>
                                      )}
                                    </div>
                                    <div 
                                      className="comment-date"
                                      style={{
                                        color: '#6c757d',
                                        fontSize: '12px',
                                        fontWeight: '400'
                                      }}
                                    >
                                      {formatDateTimeDetailed(comment.created_at)}
                                    </div>
                                  </div>
                                  <div 
                                    className="comment-text"
                                    style={{
                                      color: '#212529',
                                      lineHeight: '1.5',
                                      fontSize: '14px',
                                      whiteSpace: 'pre-wrap'
                                    }}
                                  >
                                    {comment.text}
                                  </div>
                                </div>
                              ))
                          ) : (
                            <div className="no-comments" style={{
                              textAlign: 'center',
                              padding: '40px 20px',
                              color: '#6c757d'
                            }}>
                              <div style={{ fontSize: '48px', marginBottom: '12px' }}>💬</div>
                              <div style={{ fontSize: '16px', fontWeight: '500' }}>No comments yet</div>
                              <div style={{ fontSize: '14px', marginTop: '4px' }}>Be the first to add a comment!</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer Actions */}
                  <div className="modal-footer">
                    {selectedTaskForModal.permalink_url && (
                      <a 
                        href={selectedTaskForModal.permalink_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="asana-link-button"
                      >
                        <span className="link-icon">🔗</span>
                        View in Asana
                      </a>
                    )}
                    <div className="task-timestamp">
                      by: {selectedTaskForModal.created_by} at {formatDateTimeDetailed(selectedTaskForModal.created_at)}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Narrative Results Modal */}
      {showNarrativeModal && narrativeResults && (
        <div className="modal-overlay" onClick={() => setShowNarrativeModal(false)}>
          <div className="modal-content narrative-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="narrative-header-content">
                <h3>📖 Strategic Narrative</h3>
                <div className="narrative-meta">
                  {narrativeResults.metadata && (
                    <>
                      <span className="task-count-badge">
                        {narrativeResults.metadata.taskCount} Task{narrativeResults.metadata.taskCount > 1 ? 's' : ''}
                      </span>
                      <span className="entitlement-badge">
                        {formatCompactCurrency(narrativeResults.metadata.totalEntitlement)} Total
                      </span>
                      <span className="hc-badge">
                        {formatNumber(narrativeResults.metadata.totalSdeHeadcount)} SDE HC
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button 
                className="modal-close"
                onClick={() => setShowNarrativeModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body narrative-modal-body">
              {narrativeResults.success ? (
                <div className="narrative-content">
                  {narrativeResults.processedContent?.strategicNarrative ? (
                    <div className="iris-narrative">
                      <div className="narrative-section">
                        <div className="narrative-section-content">
                          <div className="strategic-narrative-text" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                            {narrativeResults.processedContent.strategicNarrative}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="raw-narrative">
                      <div className="narrative-section">
                        <div className="narrative-section-content">
                          <div className="narrative-raw-content" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                            {narrativeResults.rawResponse}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="narrative-error">
                  <div className="error-icon">❌</div>
                  <div className="error-content">
                    <h4>Narrative Generation Failed</h4>
                    <p>{narrativeResults.error}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer narrative-modal-footer">
              <div className="narrative-footer-info">
                {narrativeResults.metadata && (
                  <span className="processing-time">
                    Generated in {(narrativeResults.metadata.processingTime / 1000).toFixed(1)}s
                  </span>
                )}
              </div>
              <div className="narrative-footer-actions">
                <button
                  onClick={() => {
                    if (narrativeResults.success) {
                      const content = formatNarrativeForDisplay(narrativeResults);
                      navigator.clipboard.writeText(content).then(() => {
                        alert('Narrative copied to clipboard!');
                      }).catch(() => {
                        alert('Failed to copy to clipboard');
                      });
                    }
                  }}
                  className="btn btn-secondary"
                  disabled={!narrativeResults.success}
                >
                  📋 Copy to Clipboard
                </button>
                <button
                  onClick={() => setShowNarrativeModal(false)}
                  className="btn btn-primary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskPrioritization;
